class MetalPosterViewer {
    constructor(canvasId, initialImageUrl = null, metalTextureUrl = null, gradientCenterColor = '#2b5174', gradientEdgeColor = '#223a52', magnetOnly = false) {
        this.canvas = document.getElementById(canvasId);
        this.container = this.canvas ? this.canvas.parentElement : null;
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.controls = null;
        this.metalPoster = null;
        this.metalPosterGrid = []; // Array to hold 4 posters for large size
        this.isGridMode = false;

        // Per-plate size used when the Large 2x2 grid is built. Portrait by
        // default; updateToLargeGridLandscape() swaps them. Every place that
        // lays the grid out now reads these instead of hardcoding 16x23, which
        // is what made a landscape Large fall back to a single plate.
        this.gridPlateWidth = 16;
        this.gridPlateHeight = 23;

        // Rotation applied to the BACK (branded metal) texture. The artwork is
        // authored portrait, so a portrait plate needs 180 degrees; a landscape
        // plate needs 90. Held here so every path that builds a back texture
        // applies it AT LOAD TIME. Correcting it after the fact is what made the
        // back flash the wrong way round before snapping into place.
        this.backTextureRotation = Math.PI;

        // ---- Texture caches -------------------------------------------------
        // Every size change tears the plate down and rebuilds it, and every
        // rebuild re-ran TextureLoader from scratch: the new material is created
        // with NO map, added to the scene, and only textured once the load
        // resolves. The frames in between are the flicker — a bare white plate
        // where the branded back should be. The back's URL never changes, and a
        // size change re-applies the SAME front image, so nothing about that
        // reload was necessary. Cached entries apply SYNCHRONOUSLY, so a rebuilt
        // plate is fully textured in the very first frame it is rendered.
        this._backTex = null;          // the one decoded metal texture, shared by every back face
        this._backTexUrl = null;
        this._backTexPromise = null;
        this._frontTexCache = new Map();  // url -> { texture, glossMap }
        this._frontTexCacheMax = 4;       // small: blob URLs from the cropper churn
        this.lights = [];
        this.currentImageUrl = initialImageUrl;
        this.metalTextureUrl = metalTextureUrl;
        this.gradientCenterColor = gradientCenterColor;
        this.gradientEdgeColor = gradientEdgeColor;

        // MAGNET-ONLY: when true the metal poster is never shown and the rubber
        // magnet is centered (z=0) so a magnet product page shows ONLY the magnet.
        this.magnetOnly = magnetOnly;

        // Info dot system
        this.posterInfoDot = null;
        this.magnetInfoDot = null;
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
        this.currentInfoBox = null;

        // Parallax system
        this.targetRotationX = 0;
        this.targetRotationY = 0;
        this.currentRotationX = 0;
        this.currentRotationY = 0;
        this.isParallaxEnabled = false;

        // Add these lines after gradient colors
        this.magnetBackgroundTexture = null;
        this.originalRendererSettings = null;

        // Payment message element for large size
        this.largePaymentMessage = null;

        // Poster dimensions (medium size default: 16x23 inches, thickness 0.5mm)
        this.posterWidth = 16;
        this.posterHeight = 23;
        this.magnetSizeOverride = null;   // see setMagnetSize()
        this.posterThickness = 0.05; // 0.5mm scaled to scene units

        // View mode settings
        this.viewMode = 'metal'; // 'metal' or 'magnet'
        this.rubberMagnet = null;
        this.rubberMagnetGrid = [];

        this.slideshowImageLoading = false; // Add loading flag for slideshow image

        if (!this.canvas) {
            console.error('Canvas element not found:', canvasId);
            return;
        }

        if (!this.container) {
            console.error('Container element not found for canvas:', canvasId);
            return;
        }

        this.init();
    }

    init() {
        this.setupScene();
        this.setupCamera();
        this.setupRenderer();
        this.setupEnvironment();
        this.setupLights();
        this.createMetalPoster();
        this.setupControls();
        this.setupEventListeners();
        this.setupVisibilityGate();
        this.animate();

        // MAGNET-ONLY: start directly in magnet view so the poster never flashes.
        if (this.magnetOnly) {
            this.switchToMagnetView();
        }

        // Trigger medium size price update after initialization
        setTimeout(() => {
            this.triggerMediumSizePricing();
        }, 100);

        /* The loading screen is hidden by the section as soon as the plate is
           actually textured and drawn. A blind 1s timer here used to uncover an
           empty scene on a slow connection. */
    }

    triggerMediumSizePricing() {
        // The viewer starts with medium size (16x23) by default
        // No need to trigger any button clicks since medium is already the default
        console.log('Viewer initialized with medium size (16x23) - no button clicking needed');

        // Just dispatch a gentle custom event for any listeners that need to know the viewer is ready
        const viewerReadyEvent = new CustomEvent('metalViewerReady', {
            detail: {
                size: 'medium',
                width: 16,
                height: 23,
                dimensions: '16x23'
            },
            bubbles: true
        });

        // Only dispatch on the canvas element to avoid interference
        if (this.canvas) {
            this.canvas.dispatchEvent(viewerReadyEvent);
        }
    }

    setupScene() {
        this.scene = new THREE.Scene();
        // Default to dark gray background (metal poster mode)
        this.scene.background = new THREE.Color(0x1a1a1a);

        // Add subtle fog for depth
        this.scene.fog = new THREE.Fog(0x1a1a1a, 50, 200);

        // Initialize slideshow properties
        this.slideshowImages = [];
        this.currentSlideIndex = 0;
        this.slideshowInterval = null;
        this.slideshowPlane = null;
        this.fadingPlane = null;
        this.currentSlideshowTexture = null;
        this.nextSlideshowTexture = null;

        // Set default slideshow images for demo
        this.setDefaultSlideshowImages();
    }

    createRadialGradientBackground(centerColor, edgeColor) {
        // Create canvas for gradient
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const context = canvas.getContext('2d');

        // Create radial gradient
        const gradient = context.createRadialGradient(256, 256, 0, 256, 256, 256);
        gradient.addColorStop(0, centerColor); // Center color
        gradient.addColorStop(1, edgeColor);   // Edge color

        // Fill canvas with gradient
        context.fillStyle = gradient;
        context.fillRect(0, 0, 512, 512);

        // Create texture from canvas
        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;

        return texture;
    }

    createUnlitMagnetBackground(centerColor, edgeColor) {
        // Store original renderer settings if not already stored
        if (!this.originalRendererSettings) {
            this.originalRendererSettings = {
                toneMapping: this.renderer.toneMapping,
                toneMappingExposure: this.renderer.toneMappingExposure
            };
        }

        // Completely disable tone mapping and set neutral exposure for true colors
        this.renderer.toneMapping = THREE.NoToneMapping;
        this.renderer.toneMappingExposure = 1.0; // Neutral exposure for true colors

        // Load the magnet background image
        const magnetBackgroundUrl = 'https://cdn.shopify.com/s/files/1/0693/1070/9938/files/magnet-bc.png?v=1753612091';

        const textureLoader = new THREE.TextureLoader();
        textureLoader.setCrossOrigin('anonymous');

        textureLoader.load(
            magnetBackgroundUrl,
            (texture) => {
                // Configure texture for unlit background
                texture.wrapS = THREE.ClampToEdgeWrapping;
                texture.wrapT = THREE.ClampToEdgeWrapping;
                texture.minFilter = THREE.LinearFilter;
                texture.magFilter = THREE.LinearFilter;
                texture.encoding = THREE.sRGBEncoding; // Proper encoding for image

                // Create separate background scene and mesh for complete lighting isolation
                this.backgroundScene = new THREE.Scene();
                this.backgroundCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

                const backgroundGeometry = new THREE.PlaneGeometry(2, 2);
                const backgroundMaterial = new THREE.MeshBasicMaterial({
                    map: texture,
                    depthTest: false,
                    depthWrite: false,
                    side: THREE.DoubleSide
                });

                this.backgroundMesh = new THREE.Mesh(backgroundGeometry, backgroundMaterial);
                this.backgroundScene.add(this.backgroundMesh);

                console.log('Loaded magnet background image successfully with unlit material');
            },
            (progress) => {
                console.log('Loading magnet background:', Math.round((progress.loaded / progress.total) * 100) + '%');
            },
            (error) => {
                console.error('Failed to load magnet background image, falling back to gradient:', error);
                // Fallback to gradient if image fails to load
                const gradientTexture = this.createRadialGradientBackground(centerColor, edgeColor);
                gradientTexture.encoding = THREE.sRGBEncoding;
                this.scene.background = gradientTexture;
            }
        );
    }

    clearMagnetBackgroundPlane() {
        // Restore original renderer settings
        if (this.originalRendererSettings) {
            this.renderer.toneMapping = this.originalRendererSettings.toneMapping;
            this.renderer.toneMappingExposure = this.originalRendererSettings.toneMappingExposure;
        }

        // Remove background scene and mesh
        if (this.backgroundScene) {
            this.backgroundScene.remove(this.backgroundMesh);
            this.backgroundScene = null;
            this.backgroundMesh = null;
            this.backgroundCamera = null;
        }

        // Reset scene background
        this.scene.background = null;

        // Restore fog for metal view
        this.scene.fog = new THREE.Fog(0x1a1a1a, 50, 200);
    }

    createInfoDot(color = 0x00ff00, size = 0.2) {
        // Create a small glowing sphere for the info dot
        const dotGeometry = new THREE.SphereGeometry(size, 16, 16);
        const dotMaterial = new THREE.MeshBasicMaterial({
            color: color,
            transparent: true,
            opacity: 0.8
        });
        const dot = new THREE.Mesh(dotGeometry, dotMaterial);

        // Add a subtle glow effect
        const glowGeometry = new THREE.SphereGeometry(size * 1.5, 16, 16);
        const glowMaterial = new THREE.MeshBasicMaterial({
            color: color,
            transparent: true,
            opacity: 0.3
        });
        const glow = new THREE.Mesh(glowGeometry, glowMaterial);

        // Group dot with glow
        const dotGroup = new THREE.Group();
        dotGroup.add(dot);
        dotGroup.add(glow);
        dotGroup.userData = { isInfoDot: true };

        return dotGroup;
    }

    createInfoDots() {
        // Clear existing info dots
        this.clearInfoDots();

        // MAGNET-ONLY: no poster, so skip the explanatory info dots entirely for
        // a clean "just the magnet" presentation.
        if (this.magnetOnly) return;

        // Create poster info dot (blue)
        this.posterInfoDot = this.createInfoDot(0x4a90e2, 0.3);
        this.posterInfoDot.userData.type = 'poster';
        this.posterInfoDot.userData.info = {
            title: 'Metal Poster',
            description: 'High-quality metal poster with vibrant colors and durable finish. Perfect for home or office decoration.'
        };
        this.scene.add(this.posterInfoDot);

        // Create magnet info dot (green)
        this.magnetInfoDot = this.createInfoDot(0x7ed321, 0.25);
        this.magnetInfoDot.userData.type = 'magnet';
        this.magnetInfoDot.userData.info = {
            title: 'Rubber Magnet',
            description: 'Strong rubber magnet with adhesive backing. Provides secure mounting with powerful magnetic hold.'
        };
        this.scene.add(this.magnetInfoDot);

        console.log('Created info dots');
    }

    updateInfoDotPositions() {
        if (this.viewMode !== 'magnet') return;

        if (this.posterInfoDot) {
            if (this.isGridMode && this.metalPosterGrid.length > 0) {
                // Position exactly on the left edge of the first poster in grid mode
                const poster = this.metalPosterGrid[0];
                this.posterInfoDot.position.set(
                    poster.position.x - (this.posterWidth / 2), // Left edge
                    poster.position.y,                           // Center vertically
                    poster.position.z                            // Exactly on the surface
                );
            } else if (this.metalPoster) {
                // Position exactly on the left edge of single poster
                this.posterInfoDot.position.set(
                    this.metalPoster.position.x - (this.posterWidth / 2), // Left edge
                    this.metalPoster.position.y,                           // Center vertically
                    this.metalPoster.position.z                            // Exactly on the surface
                );
            }
        }

        if (this.magnetInfoDot && this.rubberMagnet) {
            // Determine magnet dimensions based on current size
            let magnetWidth;
            if (this.magnetSizeOverride) {
                magnetWidth = this.magnetSizeOverride.width;
            } else if (this.posterWidth === 11.13) { // Small size
                magnetWidth = 2.5;
            } else { // Medium and Large sizes
                magnetWidth = 5;
            }

            if (this.isGridMode && this.rubberMagnet.children && this.rubberMagnet.children.length > 0) {
                // Position exactly on the left edge of the first magnet in grid mode
                const magnet = this.rubberMagnet.children[0];
                this.magnetInfoDot.position.set(
                    magnet.position.x - (magnetWidth / 2), // Left edge
                    magnet.position.y,                      // Center vertically
                    this.rubberMagnet.position.z            // Exactly on the surface
                );
            } else {
                // Position exactly on the left edge of single magnet
                this.magnetInfoDot.position.set(
                    this.rubberMagnet.position.x - (magnetWidth / 2), // Left edge
                    this.rubberMagnet.position.y,                      // Center vertically
                    this.rubberMagnet.position.z                       // Exactly on the surface
                );
            }
        }
    }

    clearInfoDots() {
        if (this.posterInfoDot) {
            this.scene.remove(this.posterInfoDot);
            this.posterInfoDot = null;
        }
        if (this.magnetInfoDot) {
            this.scene.remove(this.magnetInfoDot);
            this.magnetInfoDot = null;
        }
        this.hideInfoBox();
    }

    autoRotateToShowMagnet() {
        if (!this.rubberMagnet || !this.camera) {
            console.log('No magnet or camera found for auto-rotation');
            return;
        }

        if (!this.isObjectInView(this.rubberMagnet)) {
            console.log('Magnet not in view - starting auto-rotation');
            this.smoothRotateToShowMagnet();
        } else {
            console.log('Magnet is already in view - no rotation needed');
        }
    }

    smoothRotateToShowMagnet() {
        if (!this.rubberMagnet || !this.controls) {
            console.log('No magnet or controls found for smooth rotation');
            return;
        }

        // MAGNET-ONLY: the magnet is already centered in front of the camera, so
        // there is nothing to rotate toward — skip the poster-relative reframing.
        if (this.magnetOnly) {
            console.log('Magnet-only mode: magnet already centered, skipping reframe');
            return;
        }

        console.log('Starting smooth rotation to show magnet...');

        const magnetPos = new THREE.Vector3();
        this.rubberMagnet.getWorldPosition(magnetPos);

        // Calculate target rotation to show magnet at a good angle
        const currentCameraPos = this.camera.position.clone();
        const currentTarget = this.controls.target.clone();

        // Target position: position camera to show both poster and magnet at optimal angle
        const distance = 40; // Fixed distance for consistent framing
        const targetCameraPos = new THREE.Vector3(
            20,  // Move camera to the right side
            5,   // Slightly above center
            25   // Forward enough to see both objects
        );

        // Target to look at a point between poster and magnet
        const centerTarget = new THREE.Vector3(0, 0, -5);

        console.log('Current camera pos:', currentCameraPos);
        console.log('Target camera pos:', targetCameraPos);
        console.log('Current target:', currentTarget);
        console.log('Target look-at:', centerTarget);

        const duration = 1500; // 1.5 second smooth rotation
        const startTime = Date.now();
        const startCameraPos = currentCameraPos.clone();
        const startTarget = currentTarget.clone();

        // Disable controls during animation
        this.controls.enabled = false;

        const animateRotation = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Smooth ease-in-out
            const easedProgress = progress < 0.5
                ? 2 * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 2) / 2;

            // Interpolate camera position
            this.camera.position.lerpVectors(startCameraPos, targetCameraPos, easedProgress);

            // Interpolate target (look at center between poster and magnet)
            this.controls.target.lerpVectors(startTarget, centerTarget, easedProgress);

            this.controls.update();

            if (progress < 1) {
                requestAnimationFrame(animateRotation);
            } else {
                // Re-enable controls
                this.controls.enabled = true;
                console.log('Auto-rotation to show magnet complete');
            }
        };

        animateRotation();
    }

    showInfoBox(infoDot, mouseX, mouseY) {
        this.hideInfoBox();

        const info = infoDot.userData.info;
        if (!info) return;

        // Create info box element
        const infoBox = document.createElement('div');
        infoBox.className = 'info-box';
        infoBox.innerHTML = `
            <h3>${info.title}</h3>
            <p>${info.description}</p>
        `;

        // Style the info box
        infoBox.style.cssText = `
            position: absolute;
            background: rgba(0, 0, 0, 0.9);
            color: white;
            padding: 12px 16px;
            border-radius: 8px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 14px;
            max-width: 250px;
            pointer-events: none;
            z-index: 1000;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            border: 1px solid rgba(255,255,255,0.1);
            left: ${mouseX + 10}px;
            top: ${mouseY - 10}px;
        `;

        infoBox.querySelector('h3').style.cssText = `
            margin: 0 0 8px 0;
            font-size: 16px;
            font-weight: 600;
            color: #4a90e2;
        `;

        infoBox.querySelector('p').style.cssText = `
            margin: 0;
            line-height: 1.4;
            color: #e0e0e0;
        `;

        document.body.appendChild(infoBox);
        this.currentInfoBox = infoBox;

        console.log('Showing info box for:', info.title);
    }

    hideInfoBox() {
        if (this.currentInfoBox) {
            document.body.removeChild(this.currentInfoBox);
            this.currentInfoBox = null;
        }
    }

    showLargePaymentMessage() {
        // Remove existing message if any
        this.hideLargePaymentMessage();

        // Retired: every size is prepaid now, so Large no longer needs its own
        // notice. The rest of this method is kept but never runs.
        return;

        // Try to find the product details section where size buttons and descriptions are
        let targetContainer = null;

        // Look for common selectors for product info sections
        const possibleSelectors = [
            '.product-info',
            '.product-details',
            '.product-form',
            '.product__info',
            '.product__details',
            '.shopify-product-form',
            '[class*="product"][class*="info"]',
            '[class*="product"][class*="detail"]'
        ];

        for (const selector of possibleSelectors) {
            targetContainer = document.querySelector(selector);
            if (targetContainer) break;
        }

        // If no product container found, try to find by common product page elements
        if (!targetContainer) {
            // Look for elements that contain size buttons or buy buttons
            const buttons = document.querySelectorAll('button, .btn, [class*="button"]');
            for (const button of buttons) {
                const text = button.textContent.toLowerCase();
                if (text.includes('add to cart') || text.includes('buy now') || text.includes('size')) {
                    targetContainer = button.closest('[class*="product"], .product-form, .product-info, main, .main');
                    if (targetContainer) break;
                }
            }
        }

        // If still no container, look for description and try to find its parent
        if (!targetContainer) {
            const descriptions = document.querySelectorAll('[class*="description"], .description, [class*="desc"]');
            if (descriptions.length > 0) {
                targetContainer = descriptions[0].parentElement;
            }
        }

        // Fallback to body if nothing found
        if (!targetContainer) {
            targetContainer = document.body;
        }

        // Create the payment message element
        const messageDiv = document.createElement('div');
        messageDiv.id = 'large-size-payment-message';
        messageDiv.style.cssText = `
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            color: #856404;
            padding: 12px 16px;
            margin: 16px 0;
            border-radius: 6px;
            font-size: 14px;
            line-height: 1.4;
            display: block;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        `;

        messageDiv.innerHTML = `
            <strong>⚠️ Large Size Payment Notice:</strong><br>
            Payment for large size posters must be made online after placing your order.
            Cash on Delivery (COD) is not available for large size orders.
        `;

        // Find the best insertion point - after description, before any dropdowns
        let insertAfterElement = null;
        let insertBeforeElement = null;

        // Step 1: Look for product description to insert after
        const descriptions = targetContainer.querySelectorAll('[class*="description"], .description, [class*="desc"]');
        if (descriptions.length > 0) {
            insertAfterElement = descriptions[descriptions.length - 1]; // Use last description
        }

        // Step 2: Look for dropdowns/accordions to insert before
        const dropdownElements = targetContainer.querySelectorAll('[class*="shipping"], [class*="mounting"], [class*="accordion"], .collapsible, details, [class*="dropdown"]');
        if (dropdownElements.length > 0) {
            insertBeforeElement = dropdownElements[0]; // Insert before first dropdown
        }

        // Step 3: Insert the message in the right spot
        if (insertAfterElement) {
            // Insert right after the description
            if (insertAfterElement.nextSibling) {
                insertAfterElement.parentNode.insertBefore(messageDiv, insertAfterElement.nextSibling);
            } else {
                insertAfterElement.parentNode.appendChild(messageDiv);
            }
        } else if (insertBeforeElement) {
            // No description found, but found dropdowns - insert before them
            insertBeforeElement.parentNode.insertBefore(messageDiv, insertBeforeElement);
        } else {
            // Fallback: just append to the container
            targetContainer.appendChild(messageDiv);
        }

        this.largePaymentMessage = messageDiv;
        console.log('Large size payment message shown');
    }

    hideLargePaymentMessage() {
        if (this.largePaymentMessage) {
            this.largePaymentMessage.remove();
            this.largePaymentMessage = null;
            console.log('Large size payment message hidden');
        }
    }

    setSlideshowImages(imageUrls) {
        this.slideshowImages = imageUrls.filter(url => url && url.length > 0);
        console.log('Slideshow images loaded:', this.slideshowImages.length);
    }

    setDefaultSlideshowImages() {
        // Default slideshow images for demo
        const defaultImages = [
            'https://via.placeholder.com/1920x1080/FF6B6B/ffffff?text=Slideshow+Image+1',
            'https://via.placeholder.com/1920x1080/4ECDC4/ffffff?text=Slideshow+Image+2',
            'https://via.placeholder.com/1920x1080/45B7D1/ffffff?text=Slideshow+Image+3',
            'https://via.placeholder.com/1920x1080/96CEB4/ffffff?text=Slideshow+Image+4',
            'https://via.placeholder.com/1920x1080/FFEAA7/333333?text=Slideshow+Image+5'
        ];
        this.setSlideshowImages(defaultImages);
        console.log('Default slideshow images set');
    }

    startSlideshow() {
        if (this.slideshowImages.length === 0) {
            console.warn('No slideshow images available');
            return;
        }
        this.stopSlideshow();
        this.currentSlideIndex = 0;
        // Set placeholder background while loading first image
        this.scene.background = new THREE.Color(0x000000); // or 0x1a1a1a for dark gray
        // Show first slide without fade
        this.showSlide(0, false);
        if (this.slideshowImages.length > 1) {
            this.slideshowInterval = setInterval(() => {
                this.currentSlideIndex = (this.currentSlideIndex + 1) % this.slideshowImages.length;
                // Use fade for automatic transitions
                this.showSlide(this.currentSlideIndex, true);
            }, 10000); // Change every 10 seconds
        }
    }

    stopSlideshow() {
        if (this.slideshowInterval) {
            clearInterval(this.slideshowInterval);
            this.slideshowInterval = null;
        }

        // Clean up slideshow textures
        if (this.currentSlideshowTexture) {
            this.currentSlideshowTexture.dispose();
            this.currentSlideshowTexture = null;
        }

        if (this.nextSlideshowTexture) {
            this.nextSlideshowTexture.dispose();
            this.nextSlideshowTexture = null;
        }

        // Remove plane if leaving slideshow
        if (this.viewMode !== 'slideshow') {
            this.removeSlideshowBackgroundPlane();
        }
    }

    showSlide(index, withFade = true) {
        if (index >= 0 && index < this.slideshowImages.length) {
            const textureLoader = new THREE.TextureLoader();
            textureLoader.setCrossOrigin('anonymous');
            this.slideshowImageLoading = true; // Set loading flag
            textureLoader.load(
                this.slideshowImages[index],
                (texture) => {
                    console.log('Texture loaded, processing for slideshow...');

                    // IMMEDIATELY create cropped version to prevent any raw texture from showing
                    this.createAndApplyCroppedTexture(texture, withFade);

                    this.slideshowImageLoading = false; // Clear loading flag after processed
                    console.log(`Showed slide ${index + 1}/${this.slideshowImages.length} ${withFade ? '(with fade)' : '(instant)'}`);
                },
                undefined,
                (error) => {
                    console.error('Error loading slideshow image:', error);
                    this.slideshowImageLoading = false;
                }
            );
        }
    }

    createAndApplyCroppedTexture(texture, withFade) {
        const img = texture.image;
        const canvas = this.canvas;

        if (!img || !canvas) {
            console.warn('No image or canvas available for cropping');
            return;
        }

        // Create cropped canvas immediately - this is the ONLY texture that should ever be shown
        const croppedCanvas = document.createElement('canvas');
        const ctx = croppedCanvas.getContext('2d');

        // Set canvas size to match viewer canvas for proper aspect ratio
        croppedCanvas.width = canvas.clientWidth;
        croppedCanvas.height = canvas.clientHeight;

        // Draw image with cover behavior (center-cropped) - this prevents compression
        this.drawImageCover(ctx, img, croppedCanvas.width, croppedCanvas.height);

        // Force canvas to complete all drawing operations
        ctx.getImageData(0, 0, 1, 1);

        // Create texture from the already-cropped canvas
        const croppedTexture = new THREE.CanvasTexture(croppedCanvas);
        croppedTexture.wrapS = THREE.ClampToEdgeWrapping;
        croppedTexture.wrapT = THREE.ClampToEdgeWrapping;
        croppedTexture.minFilter = THREE.LinearFilter;
        croppedTexture.magFilter = THREE.LinearFilter;
        croppedTexture.encoding = THREE.sRGBEncoding;
        croppedTexture.needsUpdate = true;

        // Apply based on fade requirement
        if (withFade && this.currentSlideshowTexture) {
            this.fadeToNextSlideCropped(croppedTexture);
        } else {
            // Clean up old texture
            if (this.currentSlideshowTexture) {
                this.currentSlideshowTexture.dispose();
            }

            this.currentSlideshowTexture = croppedTexture;
            this.scene.background = croppedTexture;

            console.log('Applied pre-cropped slideshow texture (no fade)');
            console.log('Original dimensions:', `${img.width}x${img.height}`);
            console.log('Cropped dimensions:', `${croppedCanvas.width}x${croppedCanvas.height}`);
        }
    }

    fadeToNextSlideCropped(croppedTexture) {
        console.log('Starting fade animation with pre-cropped textures...');

        const currentTexture = this.currentSlideshowTexture;

        if (!currentTexture) {
            // No current texture, just apply the new one
            this.currentSlideshowTexture = croppedTexture;
            this.scene.background = croppedTexture;
            console.log('No current texture - applied new cropped texture directly');
            return;
        }

        // Create a canvas for blending the two already-cropped textures
        const canvas = this.canvas;
        const blendCanvas = document.createElement('canvas');
        const ctx = blendCanvas.getContext('2d');

        // Set blend canvas to match viewer canvas size (both textures are already this size)
        blendCanvas.width = canvas.clientWidth;
        blendCanvas.height = canvas.clientHeight;

        // Get the image data from both cropped textures
        const currentImage = this.getImageFromTexture(currentTexture);
        const nextImage = this.getImageFromTexture(croppedTexture);

        const fadeInDuration = 1000; // 1 second fade
        const startTime = Date.now();

        const animateFade = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / fadeInDuration, 1);

            // Smooth fade using ease-in-out
            const easedProgress = progress < 0.5
                ? 2 * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 2) / 2;

            // Clear blend canvas
            ctx.clearRect(0, 0, blendCanvas.width, blendCanvas.height);

            // Draw current image with decreasing opacity
            if (currentImage) {
                ctx.globalAlpha = 1 - easedProgress;
                ctx.drawImage(currentImage, 0, 0, blendCanvas.width, blendCanvas.height);
            }

            // Draw next image with increasing opacity
            if (nextImage) {
                ctx.globalAlpha = easedProgress;
                ctx.drawImage(nextImage, 0, 0, blendCanvas.width, blendCanvas.height);
            }

            // Create texture from blended canvas
            const blendedTexture = new THREE.CanvasTexture(blendCanvas);
            blendedTexture.wrapS = THREE.ClampToEdgeWrapping;
            blendedTexture.wrapT = THREE.ClampToEdgeWrapping;
            blendedTexture.minFilter = THREE.LinearFilter;
            blendedTexture.magFilter = THREE.LinearFilter;
            blendedTexture.encoding = THREE.sRGBEncoding;
            blendedTexture.needsUpdate = true;

            // Apply blended texture as background
            this.scene.background = blendedTexture;

            if (progress < 1) {
                requestAnimationFrame(animateFade);
            } else {
                // Fade complete - clean up and apply final texture
                if (currentTexture) {
                    currentTexture.dispose();
                }
                this.currentSlideshowTexture = croppedTexture;
                this.scene.background = croppedTexture;
                console.log('Fade animation complete with pre-cropped textures');
            }
        };

        animateFade();
    }

    getImageFromTexture(texture) {
        if (!texture) return null;

        // If it's a CanvasTexture, return the canvas directly
        if (texture.image && texture.image.tagName === 'CANVAS') {
            return texture.image;
        }

        // If it's a regular texture with an image, return the image
        if (texture.image) {
            return texture.image;
        }

        return null;
    }

    applySlideTexture(texture) {
        // This method is now replaced by createAndApplyCroppedTexture
        // Keeping for compatibility but redirecting
        this.createAndApplyCroppedTexture(texture, false);
    }

    fadeToNextSlide(nextTexture) {
        console.log('Old fadeToNextSlide called - redirecting to new cropped method');
        // Redirect to the new method that prevents compressed images
        this.createAndApplyCroppedTexture(nextTexture, true);
    }

    // Helper to draw image with cover effect (center-cropped)
    drawImageCover(ctx, img, canvasWidth, canvasHeight) {
        const imgAspect = img.width / img.height;
        const canvasAspect = canvasWidth / canvasHeight;
        let sx, sy, sw, sh;
        if (imgAspect > canvasAspect) {
            // Image is wider: crop sides
            sw = img.height * canvasAspect;
            sh = img.height;
            sx = (img.width - sw) / 2;
            sy = 0;
        } else {
            // Image is taller: crop top/bottom
            sw = img.width;
            sh = img.width / canvasAspect;
            sx = 0;
            sy = (img.height - sh) / 2;
        }
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvasWidth, canvasHeight);
    }

    switchToSlideshowView() {
        this.viewMode = 'slideshow';

                 console.log('Switching to slideshow view...');
         console.log('Current scene children count:', this.scene.children.length);

         // Clear any magnet background plane
         this.clearMagnetBackgroundPlane();

         // Create unlit background plane for slideshow
         this.createSlideshowBackgroundPlane();

        console.log('Scene children after creating slideshow plane:', this.scene.children.length);
        console.log('Slideshow plane created:', !!this.slideshowPlane);
        console.log('Fading plane created:', !!this.fadingPlane);

        if (this.slideshowPlane) {
            console.log('Slideshow plane details:');
            console.log('- Position:', this.slideshowPlane.position);
            console.log('- Visible:', this.slideshowPlane.visible);
            console.log('- Material color:', this.slideshowPlane.material.color);
            console.log('- Geometry size:', this.slideshowPlane.geometry.parameters);
            console.log('- In scene:', this.scene.children.includes(this.slideshowPlane));
        }

        // Debug: List all scene children
        console.log('All scene children:');
        this.scene.children.forEach((child, index) => {
            console.log(`${index}: ${child.name || child.type} at position`, child.position);
        });

        // Disable controls
        this.controls.enabled = false;

        // If in grid mode, instantly set slideshow positions before animation
        let skipPositionAnimation = false;
        if (this.isGridMode) {
            const slideshowPositions = this.getSlideshowPositions();
            this.metalPosterGrid.forEach((poster, index) => {
                const targetPos = slideshowPositions[index];
                poster.position.set(targetPos.x, targetPos.y, targetPos.z);
            });
            skipPositionAnimation = true;
            console.log('Instantly positioned grid posters for consistent slideshow entry');
        }

        // Animate poster to slideshow position (higher and back)
        this.animatePosterToSlideshowPosition(skipPositionAnimation);

        // Clear magnets and info dots
        if (this.rubberMagnet) {
            this.scene.remove(this.rubberMagnet);
            this.rubberMagnet = null;
        }
        this.clearMagnetGrid();
        this.clearInfoDots();

        // Start slideshow
        this.startSlideshow();

        console.log('Switched to slideshow view - should see gray background plane');
    }

    createSlideshowBackgroundPlane() {
        // Clean up any existing slideshow plane
        if (this.slideshowPlane) {
            this.scene.remove(this.slideshowPlane);
            this.slideshowPlane.geometry.dispose();
            this.slideshowPlane.material.dispose();
            this.slideshowPlane = null;
        }
        if (this.fadingPlane) {
            this.scene.remove(this.fadingPlane);
            this.fadingPlane.geometry.dispose();
            this.fadingPlane.material.dispose();
            this.fadingPlane = null;
        }

        // Store original renderer settings
        this.originalToneMapping = this.renderer.toneMapping;
        this.originalToneMappingExposure = this.renderer.toneMappingExposure;

        // Disable tone mapping and exposure for slideshow (prevents lighting effects)
        this.renderer.toneMapping = THREE.NoToneMapping;
        this.renderer.toneMappingExposure = 1.0;

        // Don't set a gray background - keep it null until first image loads
        this.scene.background = null;

        console.log('Slideshow background setup (no lighting effects)');
    }

    removeSlideshowBackgroundPlane() {
        // Restore original renderer settings
        if (this.originalToneMapping !== undefined) {
            this.renderer.toneMapping = this.originalToneMapping;
            this.renderer.toneMappingExposure = this.originalToneMappingExposure;
        }

        // Reset the scene background
        this.scene.background = null;
    }

    // Shared function to calculate consistent default grid positions
    getDefaultGridPositions() {
        const gap = 0.1;
        const posterWidth = this.gridPlateWidth;
        const posterHeight = this.gridPlateHeight;

        return [
            { x: -(posterWidth/2 + gap/2), y: posterHeight/2 + gap/2, z: 0 },    // Top-Left
            { x: posterWidth/2 + gap/2, y: posterHeight/2 + gap/2, z: 0 },     // Top-Right
            { x: -(posterWidth/2 + gap/2), y: -(posterHeight/2 + gap/2), z: 0 }, // Bottom-Left
            { x: posterWidth/2 + gap/2, y: -(posterHeight/2 + gap/2), z: 0 }   // Bottom-Right
        ];
    }

    // Shared function to calculate consistent slideshow positions
    getSlideshowPositions() {
        if (this.isGridMode) {
            // Grid layout positions for 2x2 arrangement in slideshow position
            const gap = 0.1;
            const posterWidth = this.gridPlateWidth;
            const posterHeight = this.gridPlateHeight;
            const slideshowY = -2; // Higher Y position for large size
            const slideshowZ = -30; // Slideshow Z position

            return [
                { x: -(posterWidth/2 + gap/2), y: slideshowY + posterHeight/2 + gap/2, z: slideshowZ },    // Top-Left
                { x: posterWidth/2 + gap/2, y: slideshowY + posterHeight/2 + gap/2, z: slideshowZ },     // Top-Right
                { x: -(posterWidth/2 + gap/2), y: slideshowY - (posterHeight/2 + gap/2), z: slideshowZ }, // Bottom-Left
                { x: posterWidth/2 + gap/2, y: slideshowY - (posterHeight/2 + gap/2), z: slideshowZ }   // Bottom-Right
            ];
        } else {
            return [{ x: 0, y: -8, z: -30 }]; // Single poster slideshow position
        }
    }

    animatePosterToSlideshowPosition(skipPositionAnimation = false) {
        const targetRotation = { x: 0, y: 0, z: 0 };

        // Use shared function for consistent positioning
        const targetPositions = this.getSlideshowPositions();

        if (this.isGridMode) {
            console.log('*** ANIMATION TRANSITION: Animating grid to slideshow positions (maintaining 2x2 layout) ***');
            targetPositions.forEach((pos, index) => {
                console.log(`Animation target ${index}: (${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)})`);
            });
        }

        const targetCameraPosition = { x: 0, y: -5, z: 45 };

        console.log('Animating poster to slideshow position...');
        console.log('Target positions:', targetPositions, this.isGridMode ? '(LARGE - grid maintained)' : '(SMALL/MEDIUM - single)');
        console.log('Target camera position:', targetCameraPosition);

        const animationDuration = 1500;
        const startTime = Date.now();

        const startCameraPos = {
            x: this.camera.position.x,
            y: this.camera.position.y,
            z: this.camera.position.z
        };

        const posterObjects = this.isGridMode ? this.metalPosterGrid : [this.metalPoster];
        const startPosterStates = posterObjects.map(poster => {
            if (!poster) return null;
            return {
                rotation: { x: poster.rotation.x, y: poster.rotation.y, z: poster.rotation.z },
                position: { x: poster.position.x, y: poster.position.y, z: poster.position.z }
            };
        });

        const animateToSlideshow = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / animationDuration, 1);

            const easedProgress = progress < 0.5
                ? 4 * progress * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;

            this.camera.position.x = startCameraPos.x + (targetCameraPosition.x - startCameraPos.x) * easedProgress;
            this.camera.position.y = startCameraPos.y + (targetCameraPosition.y - startCameraPos.y) * easedProgress;
            this.camera.position.z = startCameraPos.z + (targetCameraPosition.z - startCameraPos.z) * easedProgress;

            posterObjects.forEach((poster, index) => {
                if (!poster || !startPosterStates[index]) return;

                const startState = startPosterStates[index];

                // Get target position for this specific poster
                const targetPos = targetPositions[Math.min(index, targetPositions.length - 1)];

                const rotX = this.smoothAngleInterpolation(startState.rotation.x, targetRotation.x, easedProgress);
                const rotY = this.smoothAngleInterpolation(startState.rotation.y, targetRotation.y, easedProgress);
                const rotZ = this.smoothAngleInterpolation(startState.rotation.z, targetRotation.z, easedProgress);

                poster.rotation.x = rotX;
                poster.rotation.y = rotY;
                poster.rotation.z = rotZ;

                if (!skipPositionAnimation) {
                    poster.position.x = startState.position.x + (targetPos.x - startState.position.x) * easedProgress;
                    poster.position.y = startState.position.y + (targetPos.y - startState.position.y) * easedProgress;
                    poster.position.z = startState.position.z + (targetPos.z - startState.position.z) * easedProgress;
                }
            });

            // Camera should look at center of grid (for grid mode) or single poster position
            const lookAtY = this.isGridMode ? -2 * easedProgress : -8 * easedProgress; // Grid is higher than single
            const lookAtZ = -30 * easedProgress;
            this.camera.lookAt(0, lookAtY, lookAtZ);
            this.controls.target.set(0, lookAtY, lookAtZ);
            this.controls.update();

            if (progress < 1) {
                requestAnimationFrame(animateToSlideshow);
            } else {
                console.log('*** ANIMATION COMPLETE: Poster animation to slideshow position complete',
                    this.isGridMode ? '(grid layout maintained)' : '(single poster)');

                // Log final positions for debugging
                if (this.isGridMode) {
                    this.metalPosterGrid.forEach((poster, index) => {
                        console.log(`Final animated position ${index}: (${poster.position.x.toFixed(2)}, ${poster.position.y.toFixed(2)}, ${poster.position.z.toFixed(2)})`);
                    });
                }
            }
        };

        animateToSlideshow();
    }

    smoothAngleInterpolation(startAngle, endAngle, progress) {
        const normalizeAngle = (angle) => {
            while (angle > Math.PI) angle -= 2 * Math.PI;
            while (angle < -Math.PI) angle += 2 * Math.PI;
            return angle;
        };

        const start = normalizeAngle(startAngle);
        const end = normalizeAngle(endAngle);

        let diff = end - start;
        if (diff > Math.PI) diff -= 2 * Math.PI;
        if (diff < -Math.PI) diff += 2 * Math.PI;

        return start + diff * progress;
    }

    animateFromSlideshowPosition() {
        const targetRotation = { x: 0, y: 0, z: 0 };

        // Use shared functions for consistent positioning
        const targetPositions = this.isGridMode ? this.getDefaultGridPositions() : [{ x: 0, y: 0, z: 0 }];

        if (this.isGridMode) {
            console.log('Animating grid back to individual positions using shared function');
        }

        const targetCameraPosition = { x: 0, y: 0, z: 40 };

        console.log('Animating back from slideshow position...', this.isGridMode ? '(from LARGE - higher)' : '(from SMALL/MEDIUM - lower)');

        const animationDuration = 1200;
        const startTime = Date.now();

        const startCameraPos = {
            x: this.camera.position.x,
            y: this.camera.position.y,
            z: this.camera.position.z
        };

        const posterObjects = this.isGridMode ? this.metalPosterGrid : [this.metalPoster];
        const startPosterStates = posterObjects.map(poster => {
            if (!poster) return null;
            return {
                rotation: { x: poster.rotation.x, y: poster.rotation.y, z: poster.rotation.z },
                position: { x: poster.position.x, y: poster.position.y, z: poster.position.z }
            };
        });

        const animateBack = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / animationDuration, 1);

            const easedProgress = 1 - Math.pow(1 - progress, 3);

            this.camera.position.x = startCameraPos.x + (targetCameraPosition.x - startCameraPos.x) * easedProgress;
            this.camera.position.y = startCameraPos.y + (targetCameraPosition.y - startCameraPos.y) * easedProgress;
            this.camera.position.z = startCameraPos.z + (targetCameraPosition.z - startCameraPos.z) * easedProgress;

            posterObjects.forEach((poster, index) => {
                if (!poster || !startPosterStates[index]) return;

                const startState = startPosterStates[index];

                // Get target position for this specific poster
                const targetPos = targetPositions[Math.min(index, targetPositions.length - 1)];

                poster.rotation.x = startState.rotation.x + (targetRotation.x - startState.rotation.x) * easedProgress;
                poster.rotation.y = startState.rotation.y + (targetRotation.y - startState.rotation.y) * easedProgress;
                poster.rotation.z = startState.rotation.z + (targetRotation.z - startState.rotation.z) * easedProgress;

                poster.position.x = startState.position.x + (targetPos.x - startState.position.x) * easedProgress;
                poster.position.y = startState.position.y + (targetPos.y - startState.position.y) * easedProgress;
                poster.position.z = startState.position.z + (targetPos.z - startState.position.z) * easedProgress;
            });

            this.camera.lookAt(0, 0, 0);
            this.controls.target.set(0, 0, 0);
            this.controls.update();

            if (progress < 1) {
                requestAnimationFrame(animateBack);
            } else {
                console.log('Poster animation back from slideshow complete',
                    this.isGridMode ? '(grid maintained individual positions)' : '(single poster to center)');
            }
        };

        animateBack();
    }

    setupCamera() {
        const aspect = this.container.clientWidth / this.container.clientHeight;
        this.camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 1000);
        this.camera.position.set(0, 0, 40);
    }

    setupRenderer() {
        // On a 2x/3x screen the canvas is already supersampled, so MSAA costs
        // fill rate for nothing. Keep it only where pixels are big.
        const dpr = window.devicePixelRatio || 1;
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: dpr < 1.5,
            alpha: true,
            powerPreference: 'high-performance'
        });

        this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
        this.renderer.setPixelRatio(Math.min(dpr, 2));
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.outputEncoding = THREE.sRGBEncoding;
        // Neutral tone mapping so metal view shows the product's TRUE colors,
        // matching magnet view (which already disables tone mapping).
        this.renderer.toneMapping = THREE.NoToneMapping;
        this.renderer.toneMappingExposure = 1.0;
        this.renderer.physicallyCorrectLights = true; // Better PBR support for water-like materials

        // Store original renderer settings for later restoration
        this.originalToneMapping = this.renderer.toneMapping;
        this.originalToneMappingExposure = this.renderer.toneMappingExposure;
    }

    setupEnvironment() {
        try {
            // Neutral GRAYSCALE studio environment so glossy (white-ink) areas
            // have something bright to reflect — while staying colour-neutral
            // so the product's true colours are preserved.
            const c = document.createElement('canvas');
            c.width = 512; c.height = 256;
            const ctx = c.getContext('2d');
            const g = ctx.createLinearGradient(0, 0, 0, 256);
            g.addColorStop(0.0, '#ffffff');
            g.addColorStop(0.35, '#dcdcdc');
            g.addColorStop(0.65, '#888888');
            g.addColorStop(1.0, '#3a3a3a');
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, 512, 256);
            const hl = ctx.createRadialGradient(256, 110, 10, 256, 110, 190);
            hl.addColorStop(0, 'rgba(255,255,255,0.9)');
            hl.addColorStop(1, 'rgba(255,255,255,0)');
            ctx.fillStyle = hl;
            ctx.fillRect(0, 0, 512, 256);

            const equirect = new THREE.CanvasTexture(c);
            equirect.mapping = THREE.EquirectangularReflectionMapping;

            if (THREE.PMREMGenerator) {
                const pmrem = new THREE.PMREMGenerator(this.renderer);
                this.envMap = pmrem.fromEquirectangular(equirect).texture;
                pmrem.dispose();
                equirect.dispose();
            } else {
                this.envMap = equirect;
            }
        } catch (e) {
            console.warn('Environment setup failed; gloss will be subtle.', e);
            this.envMap = null;
        }
    }

    generateGlossMap(image) {
        // Builds a per-pixel roughness map: white => shiny, saturated colour =>
        // matte, light tints => slightly shiny. Mirrors how UV white-ink behaves.
        try {
            if (!image) return null;
            const w = image.width || image.naturalWidth;
            const h = image.height || image.naturalHeight;
            if (!w || !h) return null;

            const maxDim = 1024; // downscale big artwork; gloss map needs no full res
            let cw = w, ch = h;
            if (Math.max(w, h) > maxDim) {
                const s = maxDim / Math.max(w, h);
                cw = Math.round(w * s); ch = Math.round(h * s);
            }

            const src = document.createElement('canvas');
            src.width = cw; src.height = ch;
            const sctx = src.getContext('2d');
            sctx.drawImage(image, 0, 0, cw, ch);
            const data = sctx.getImageData(0, 0, cw, ch);
            const px = data.data;

            for (let i = 0; i < px.length; i += 4) {
                const r = px[i] / 255, gg = px[i + 1] / 255, b = px[i + 2] / 255;
                const max = Math.max(r, gg, b), min = Math.min(r, gg, b);
                const brightness = max;
                const sat = max === 0 ? 0 : (max - min) / max;

                // gloss is high only when BRIGHT and DESATURATED (i.e. white-ish)
                let gloss = brightness * (1 - sat);
                gloss = Math.pow(gloss, 1.4); // curve so only light areas get strong gloss

                let rough = 1 - gloss;
                rough = Math.max(0.12, Math.min(0.95, rough)); // believable band

                const v = Math.round(rough * 255);
                px[i] = v; px[i + 1] = v; px[i + 2] = v; px[i + 3] = 255;
            }
            sctx.putImageData(data, 0, 0);

            const tex = new THREE.CanvasTexture(src);
            tex.wrapS = THREE.ClampToEdgeWrapping;
            tex.wrapT = THREE.ClampToEdgeWrapping;
            tex.minFilter = THREE.LinearFilter;
            tex.magFilter = THREE.LinearFilter;
            tex.flipY = true; // match the colour map's flip so they line up
            tex.needsUpdate = true;
            return tex;
        } catch (e) {
            console.warn('Gloss map generation failed (image may be cross-origin); using matte.', e);
            return null;
        }
    }

    setShadowsEnabled(on) {
        this.lights.forEach((l) => {
            if (l && l.isDirectionalLight && l.shadow) {
                // The main (first) light is the only shadow caster.
                if (l === this.lights[0]) l.castShadow = !!on;
            }
        });
        if (this.renderer) this.renderer.shadowMap.needsUpdate = true;
    }

    setupLights() {
        // Main directional light
        const mainLight = new THREE.DirectionalLight(0xffffff, 1.5);
        mainLight.position.set(5, 2, 12);
        // Only the rubber magnet casts a shadow, so the shadow pass is switched
        // on when magnet view opens (setShadowsEnabled) instead of running on
        // every frame of the metal view, where nothing casts.
        mainLight.castShadow = false;
        mainLight.shadow.mapSize.width = 1024;
        mainLight.shadow.mapSize.height = 1024;
        mainLight.shadow.camera.near = 0.5;
        mainLight.shadow.camera.far = 500;
        this.scene.add(mainLight);
        this.lights.push(mainLight);

        // Fill light from the opposite side (neutral white)
        const fillLight = new THREE.DirectionalLight(0xffffff, 0.6);
        fillLight.position.set(-10, -5, 5);
        this.scene.add(fillLight);
        this.lights.push(fillLight);

        // Back light to soften shadows (not as strong as front)
        const backLight = new THREE.DirectionalLight(0xffffff, 0.8);
        backLight.position.set(0, 0, -10); // Behind the objects
        this.scene.add(backLight);
        this.lights.push(backLight);

        // Ambient light for overall illumination
        const ambientLight = new THREE.AmbientLight(0x404040, 0.3);
        this.scene.add(ambientLight);
        this.lights.push(ambientLight);

        // Rim light for edge definition (neutral white)
        const rimLight = new THREE.DirectionalLight(0xffffff, 0.4);
        rimLight.position.set(0, 0, -10);
        this.scene.add(rimLight);
        this.lights.push(rimLight);
    }

    createMetalPoster() {
        // Create the geometry with rounded corners
        const geometry = this.createRoundedBoxGeometry(
            this.posterWidth,
            this.posterHeight,
            this.posterThickness,
            0.2 // 2mm corner radius
        );

        // Create materials for ExtrudeGeometry
        // We'll use 3 materials: [front, back, sides]
        const materials = [];

        // Front face material (will have the poster image)
        const frontMaterial = new THREE.MeshLambertMaterial({
            color: 0xffffff,  // White base for product image
        });

        // Back face material (match front's Lambert for consistent lighting)
        const backMaterial = new THREE.MeshLambertMaterial({
            color: 0xffffff,  // White base for metal texture if applied
        });

        // Side faces (extruded edges) - metallic
        const edgeMaterial = new THREE.MeshStandardMaterial({
            color: 0x888888,  // Gray metal color
            metalness: 0.8,
            roughness: 0.2,
        });

        // Custom material array to match ExtrudeGeometry groups: [back, front, sides]
        materials[0] = backMaterial; // Back face (metal texture)
        materials[1] = frontMaterial; // Front face (product image)
        materials[2] = edgeMaterial; // Side faces (extruded edges)

        console.log('Rounded metal poster materials: back (metal) + front (image) + edges');

        // Create the mesh
        this.metalPoster = new THREE.Mesh(geometry, materials);
        this.metalPoster.castShadow = false; // Don't cast shadows on magnet
        this.metalPoster.receiveShadow = true;

        // MAGNET-ONLY: never show the metal poster on a magnet product page.
        if (this.magnetOnly) this.metalPoster.visible = false;

        // No rotation needed - front face should be visible by default
        console.log('Metal poster created, front face is material index 4');

        this.scene.add(this.metalPoster);

        // Load initial image if provided
        if (this.currentImageUrl) {
            console.log('Loading initial image:', this.currentImageUrl);
            this.updatePosterImage(this.currentImageUrl);
        } else {
            console.log('No initial image provided');
        }

        // Load metal texture if provided
        if (this.metalTextureUrl) {
            console.log('Loading initial metal texture:', this.metalTextureUrl);
            this.updateMetalTexture(this.metalTextureUrl);
        } else {
            console.log('No metal texture provided - using default silver color');
        }
    }



    createRubberMagnet() {
        // If in grid mode (large size), create 4 magnets
        if (this.isGridMode) {
            this.createLargeMagnetGrid();
            return;
        }

        // Keep the metal poster visible at consistent position
        if (this.metalPoster) {
            this.metalPoster.position.set(0, 0, 0); // Consistent position for all sizes
        }

        // Determine magnet dimensions based on current size
        let magnetWidth, magnetHeight;
        if (this.magnetSizeOverride) {
            // Explicitly set by the product section (see setMagnetSize).
            // Used for the Mini plate, whose magnet is 2.5 x 2.5 in.
            magnetWidth = this.magnetSizeOverride.width;
            magnetHeight = this.magnetSizeOverride.height;
        } else if (this.posterWidth === 11.13) { // Small size
            magnetWidth = 2.5;
            magnetHeight = 5;
        } else { // Medium size (16x23) and Large will use medium magnet size
            magnetWidth = 5;
            magnetHeight = 5;
        }

        const magnetThickness = 0.2; // 2mm
        const adhesiveThickness = 0.15; // 1.5mm

        // Create rubber magnet (very dark, matte)
        const magnetGeometry = new THREE.BoxGeometry(magnetWidth, magnetHeight, magnetThickness);
        const magnetMaterial = new THREE.MeshLambertMaterial({
            color: 0x020202, // Even darker, almost pure black
            roughness: 0.98, // Very matte
        });

        const magnetMesh = new THREE.Mesh(magnetGeometry, magnetMaterial);
        magnetMesh.position.z = adhesiveThickness / 2; // Offset by adhesive thickness
        magnetMesh.castShadow = true;
        magnetMesh.receiveShadow = true;

        // Create bumpy adhesive layer (pure transparent with reflections)
        const adhesiveGeometry = this.createBumpyAdhesiveGeometry(magnetWidth, magnetHeight, adhesiveThickness);
        const adhesiveMaterial = new THREE.MeshPhysicalMaterial({
            color: 0x202020, // Even darker black tone
            metalness: 0.0,
            roughness: 0.05, // Very low roughness for strong reflections
            transmission: 0.7, // Lower transmission so color shows through
            thickness: 0.5,
            ior: 1.33, // Index of refraction for water
            clearcoat: 0.8, // High clearcoat for strong reflections
            clearcoatRoughness: 0.02, // Very smooth clearcoat
            transparent: true,
            opacity: 0.4, // Higher opacity so black color is visible
        });

        const adhesiveLayer = new THREE.Mesh(adhesiveGeometry, adhesiveMaterial);
        adhesiveLayer.position.z = -magnetThickness / 2;
        adhesiveLayer.castShadow = false;
        adhesiveLayer.receiveShadow = true;

        // Add subtle outline to adhesive (using simple box for clean edges only)
        const outlineGeometry = new THREE.BoxGeometry(magnetWidth, magnetHeight, adhesiveThickness);
        const adhesiveEdges = new THREE.EdgesGeometry(outlineGeometry);
        const adhesiveOutlineMaterial = new THREE.LineBasicMaterial({
            color: 0x888888,
            opacity: 0.3,
            transparent: true,
            linewidth: 1
        });
        const adhesiveOutline = new THREE.LineSegments(adhesiveEdges, adhesiveOutlineMaterial);
        adhesiveOutline.position.copy(adhesiveLayer.position);

        // Group magnet, adhesive, and outline
        const magnetGroup = new THREE.Group();
        magnetGroup.add(magnetMesh);
        magnetGroup.add(adhesiveLayer);
        magnetGroup.add(adhesiveOutline);
        // MAGNET-ONLY: center the magnet at z=0 (no poster to sit behind). Default
        // poster mode keeps it at z=-10 so it sits behind the metal poster.
        magnetGroup.position.z = this.magnetOnly ? 0 : -10;
        magnetGroup.position.x = 0; // Center on X axis
        magnetGroup.position.y = 0; // Center on Y axis

        this.scene.add(magnetGroup);
        this.rubberMagnet = magnetGroup; // Store the group as the main object

        // Don't apply any image to the magnet - keep it dark
    }

    createBumpyAdhesiveGeometry(width, height, thickness) {
        // Create base geometry with more subdivisions for better water effect on all faces
        const geometry = new THREE.BoxGeometry(width, height, thickness, 20, 20, 6);

        // Get position attribute to modify vertices
        const position = geometry.attributes.position;
        const vertices = position.array;

        // Add organic wave-like bumps for water-like appearance on ALL surfaces
        for (let i = 0; i < vertices.length; i += 3) {
            const x = vertices[i];
            const y = vertices[i + 1];
            const z = vertices[i + 2];

            // Check if vertex is on any surface (front, back, or sides)
            const onFrontBack = Math.abs(Math.abs(z) - thickness/2) < 0.001;
            const onLeftRight = Math.abs(Math.abs(x) - width/2) < 0.001;
            const onTopBottom = Math.abs(Math.abs(y) - height/2) < 0.001;

            if (onFrontBack || onLeftRight || onTopBottom) {
                // Create subtle wave patterns for water-like reflections
                const waveX = Math.sin(x * 10 + y * 5) * 0.002;
                const waveY = Math.sin(y * 8 + z * 6) * 0.002;
                const waveZ = Math.sin(z * 12 + x * 4) * 0.002;
                const noise = (Math.random() - 0.5) * 0.003;

                // Apply bumps to the appropriate coordinate based on which surface
                if (onFrontBack) {
                    vertices[i + 2] = z + waveX + waveY + noise; // Z-direction for front/back
                }
                if (onLeftRight) {
                    vertices[i] = x + waveY + waveZ + noise; // X-direction for left/right
                }
                if (onTopBottom) {
                    vertices[i + 1] = y + waveX + waveZ + noise; // Y-direction for top/bottom
                }
            }
        }

        position.needsUpdate = true;
        geometry.computeVertexNormals(); // Recalculate normals for proper water-like reflections

        return geometry;
    }

    createRoundedBoxGeometry(width, height, depth, radius) {
        // Create a rounded rectangle shape
        const shape = new THREE.Shape();
        const x = -width / 2;
        const y = -height / 2;

        // Create rounded rectangle path
        shape.moveTo(x + radius, y);
        shape.lineTo(x + width - radius, y);
        shape.quadraticCurveTo(x + width, y, x + width, y + radius);
        shape.lineTo(x + width, y + height - radius);
        shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        shape.lineTo(x + radius, y + height);
        shape.quadraticCurveTo(x, y + height, x, y + height - radius);
        shape.lineTo(x, y + radius);
        shape.quadraticCurveTo(x, y, x + radius, y);

        // Extrude the shape
        const extrudeSettings = {
            depth: depth,
            bevelEnabled: false,
            steps: 1
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

        // Modify material groups to separate front and back faces
        geometry.clearGroups();

        // Calculate face counts for ExtrudeGeometry
        const shapeVertices = shape.getPoints().length;
        const frontFaces = shapeVertices - 2; // Triangulated front face
        const backFaces = shapeVertices - 2;  // Triangulated back face
        const sideFaces = shapeVertices * 2;  // Side faces (quads = 2 triangles each)

        // Add material groups: back (0), front (1), sides (2) - ExtrudeGeometry order
        geometry.addGroup(0, backFaces * 3, 0);  // Back faces use material 0 (metal texture)
        geometry.addGroup(backFaces * 3, frontFaces * 3, 1);  // Front faces use material 1 (product image)
        geometry.addGroup((frontFaces + backFaces) * 3, sideFaces * 3, 2);  // Side faces use material 2

        // Fix UV mapping for textures
        geometry.computeBoundingBox();

        // Update UV coordinates for proper texture mapping
        if (geometry.attributes && geometry.attributes.uv) {
            const uvArray = geometry.attributes.uv.array;
            const posArray = geometry.attributes.position.array;

            for (let i = 0; i < uvArray.length; i += 2) {
                const vertexIndex = i / 2;
                const x = posArray[vertexIndex * 3];
                const y = posArray[vertexIndex * 3 + 1];
                const z = posArray[vertexIndex * 3 + 2];

                // Map front and back faces (Z = depth or Z = 0)
                if (Math.abs(z - depth) < 0.01 || Math.abs(z) < 0.01) {
                    uvArray[i] = (x + width/2) / width;     // U coordinate
                    uvArray[i + 1] = (y + height/2) / height; // V coordinate
                }
            }

            geometry.attributes.uv.needsUpdate = true;
        }

        return geometry;
    }

    /**
     * Decode the branded metal back texture ONCE and hand the same instance to
     * every back face. Sharing is deliberate: all backs want identical settings,
     * so a single rotation change updates all of them at once, and the GPU holds
     * one copy instead of five.
     */
    loadBackTexture() {
        if (this._backTex && this._backTexUrl === this.metalTextureUrl) {
            return Promise.resolve(this._backTex);
        }
        if (this._backTexPromise && this._backTexUrl === this.metalTextureUrl) {
            return this._backTexPromise;
        }
        if (!this.metalTextureUrl) return Promise.resolve(null);

        this._backTexUrl = this.metalTextureUrl;
        this._backTexPromise = new Promise((resolve) => {
            const loader = new THREE.TextureLoader();
            loader.setCrossOrigin('anonymous');
            loader.load(
                this.cdnSized(this.metalTextureUrl, 1200),
                (texture) => {
                    texture.wrapS = THREE.ClampToEdgeWrapping;
                    texture.wrapT = THREE.ClampToEdgeWrapping;
                    texture.minFilter = THREE.LinearFilter;
                    texture.magFilter = THREE.LinearFilter;
                    texture.encoding = THREE.sRGBEncoding;
                    texture.flipY = false; // avoids left-right inversion on the back
                    texture.center.set(0.5, 0.5);
                    texture.rotation = this.backTextureRotation; // 180 portrait, 90 landscape
                    texture.needsUpdate = true;
                    this._backTex = texture;
                    resolve(texture);
                },
                undefined,
                (error) => {
                    console.error('Error loading metal back texture:', error);
                    this._backTexPromise = null;
                    resolve(null);
                }
            );
        });
        return this._backTexPromise;
    }

    /**
     * Put the metal back texture on a material. Synchronous once the texture has
     * been decoded once — which is the whole point: no frame is ever rendered
     * with a mapless back.
     */
    applyBackTexture(material) {
        if (!material) return;
        if (this._backTex && this._backTexUrl === this.metalTextureUrl) {
            if (material.map !== this._backTex) {
                material.map = this._backTex;
                material.needsUpdate = true;
            }
            return;
        }
        this.loadBackTexture().then((texture) => {
            if (!texture || !material) return;
            material.map = texture;
            material.needsUpdate = true;
        });
    }

    /**
     * Front artwork, cached by URL together with its gloss map. Re-analysing the
     * image into a roughness map is the most expensive thing this viewer does
     * per size change, and the answer is identical every time for the same URL.
     */
    /* Shopify serves whatever was uploaded unless a width is asked for. A 4000px
       original costs seconds on mobile data and gives no visible benefit: the
       plate is a few hundred pixels on screen and the gloss map is built at
       1024. Adds ?width= to Shopify CDN URLs only; blob:/data: and anything
       else is passed through untouched. */
    cdnSized(url, width) {
        try {
            if (!url || typeof url !== 'string') return url;
            if (!/\/cdn\/shop(ify)?\//.test(url) && !/cdn\.shopify\.com/.test(url)) return url;
            if (/[?&]width=/.test(url)) return url;
            return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
        } catch (e) { return url; }
    }

    loadFrontTexture(url) {
        const hit = this._frontTexCache.get(url);
        if (hit) return Promise.resolve(hit);

        return new Promise((resolve) => {
            const loader = new THREE.TextureLoader();
            loader.setCrossOrigin('anonymous');
            loader.load(
                this.cdnSized(url, 1800),
                (texture) => {
                    texture.wrapS = THREE.ClampToEdgeWrapping;
                    texture.wrapT = THREE.ClampToEdgeWrapping;
                    texture.minFilter = THREE.LinearFilter;
                    texture.magFilter = THREE.LinearFilter;
                    texture.encoding = THREE.sRGBEncoding;
                    texture.flipY = true; // fixes the upside-down front
                    const entry = { texture: texture, glossMap: this.generateGlossMap(texture.image) };

                    // Bounded: the custom-poster cropper mints a new blob URL per
                    // crop and revokes the old one, so an unbounded cache would
                    // hold textures for URLs that no longer exist.
                    this._frontTexCache.set(url, entry);
                    while (this._frontTexCache.size > this._frontTexCacheMax) {
                        const oldestKey = this._frontTexCache.keys().next().value;
                        const oldest = this._frontTexCache.get(oldestKey);
                        this._frontTexCache.delete(oldestKey);
                        try {
                            if (oldest.texture) oldest.texture.dispose();
                            if (oldest.glossMap) oldest.glossMap.dispose();
                        } catch (e) { /* already gone */ }
                    }
                    resolve(entry);
                },
                undefined,
                (error) => {
                    console.error('Error loading texture:', error, url);
                    resolve(null);
                }
            );
        });
    }

    // Kept for compatibility: older call sites pass (material, url).
    updateBackMaterialTexture(material, textureUrl) {
        if (textureUrl && textureUrl !== this.metalTextureUrl) {
            this.metalTextureUrl = textureUrl;
            this._backTex = null;
            this._backTexPromise = null;
        }
        this.applyBackTexture(material);
    }

    switchToMagnetView() {
        const wasInSlideshow = this.viewMode === 'slideshow';
        this.viewMode = 'magnet';
        this.setShadowsEnabled(true);   // the magnet is the only shadow caster

        // MAGNET-ONLY: force the metal poster (and any 2x2 poster grid) hidden every
        // time we enter magnet view, so a magnet product page never shows the poster
        // even if another code path re-created or revealed it.
        if (this.magnetOnly) {
            if (this.metalPoster) this.metalPoster.visible = false;
            if (this.metalPosterGrid && this.metalPosterGrid.length) {
                this.metalPosterGrid.forEach(function (p) { if (p) p.visible = false; });
            }
        }

        this.stopSlideshow();

        if (wasInSlideshow) {
            this.removeSlideshowBackgroundPlane();
        }

        // Disable fog for magnet view to prevent interference with background colors
        this.scene.fog = null;

        this.controls.enabled = true;

        // MAGNET-ONLY safety: remove any previously created magnet so repeated
        // calls (init + section default) never stack duplicate magnets.
        if (this.rubberMagnet) {
            this.scene.remove(this.rubberMagnet);
            this.rubberMagnet = null;
        }
        this.clearMagnetGrid();

        // Create unlit gradient background
        this.createUnlitMagnetBackground(this.gradientCenterColor, this.gradientEdgeColor);

        if (wasInSlideshow) {
            this.animateFromSlideshowPosition();
        }

        if (this.isGridMode) {
            this.createLargeMagnetGrid();
        } else {
            this.createRubberMagnet();
        }

        // Smoothly transition parallax rotations to 0
        this.targetRotationX = 0;
        this.targetRotationY = 0;
        // Don't reset currentRotation values - let animate loop smoothly interpolate to targets

        // Create info dots for magnet view
        this.createInfoDots();

        // Auto-rotate to show magnet if not visible (with small delay to ensure magnet is fully created)
        setTimeout(() => {
            this.autoRotateToShowMagnet();
        }, 100);

        console.log('Reset rotations for magnet view');
    }

    switchToMetalView() {
        this.setShadowsEnabled(false);
        const wasInSlideshow = this.viewMode === 'slideshow';
        this.viewMode = 'metal';

        // Stop slideshow if running
        this.stopSlideshow();

                 // Remove slideshow background plane if coming from slideshow
         if (wasInSlideshow) {
             this.removeSlideshowBackgroundPlane();
         }

         // Clear any magnet background plane (this will restore renderer settings and fog)
         this.clearMagnetBackgroundPlane();

         // Re-enable controls
         this.controls.enabled = true;

        // Switch back to dark gray background for metal mode
        this.scene.background = new THREE.Color(0x1a1a1a);

        // Clear magnet objects and info dots
        if (this.rubberMagnet) {
            this.scene.remove(this.rubberMagnet);
            this.rubberMagnet = null;
        }
        this.clearMagnetGrid();
        this.clearInfoDots();

        // Animate back from slideshow if coming from slideshow
        if (wasInSlideshow) {
            this.animateFromSlideshowPosition();
        } else {
            // Reset metal poster position to center if not coming from slideshow
        if (this.metalPoster) {
            this.metalPoster.position.x = 0;
            this.metalPoster.position.y = 0;
            this.metalPoster.position.z = 0; // Reset Z to center
        }

        // Reset grid positions if in grid mode
        if (this.isGridMode && this.metalPosterGrid.length > 0) {
            // Reset grid positions to center
            const gap = 0.1;
            const posterWidth = this.gridPlateWidth;
            const posterHeight = this.gridPlateHeight;

            const positions = [
                { x: -(posterWidth/2 + gap/2), y: posterHeight/2 + gap/2 },
                { x: posterWidth/2 + gap/2, y: posterHeight/2 + gap/2 },
                { x: -(posterWidth/2 + gap/2), y: -(posterHeight/2 + gap/2) },
                { x: posterWidth/2 + gap/2, y: -(posterHeight/2 + gap/2) }
            ];

            this.metalPosterGrid.forEach((poster, index) => {
                if (positions[index]) {
                    poster.position.x = positions[index].x;
                    poster.position.y = positions[index].y;
                    poster.position.z = 0; // Reset Z position to center
                }
            });
            }
        }
    }

    createRubberMagnetGrid() {
        // Clear existing magnet grid
        this.clearMagnetGrid();

        // Move metal poster grid forward
        if (this.metalPosterGrid.length > 0) {
            this.metalPosterGrid.forEach(poster => {
                poster.position.z += 4; // Move forward
                // Keep original X and Y positions
            });
        }

        // Create magnet grid behind
        const gap = 0.1;
        const magnetWidth = 5;
        const magnetHeight = 5;
        const magnetThickness = 0.2;
        const adhesiveThickness = 0.15;
        const offsetZ = -10; // Position magnets behind (increased distance)

        const positions = [
            { x: -(magnetWidth/2 + gap/2), y: magnetHeight/2 + gap/2 },
            { x: magnetWidth/2 + gap/2, y: magnetHeight/2 + gap/2 },
            { x: -(magnetWidth/2 + gap/2), y: -(magnetHeight/2 + gap/2) },
            { x: magnetWidth/2 + gap/2, y: -(magnetHeight/2 + gap/2) }
        ];

        positions.forEach((pos, index) => {
            // Create magnet (very dark, matte)
            const magnetGeometry = new THREE.BoxGeometry(magnetWidth, magnetHeight, magnetThickness);
            const magnetMaterial = new THREE.MeshLambertMaterial({
                color: 0x020202, // Even darker, almost pure black
                roughness: 0.98, // Very matte
            });
            const magnet = new THREE.Mesh(magnetGeometry, magnetMaterial);

            // Create bumpy adhesive (pure transparent with reflections)
            const adhesiveGeometry = this.createBumpyAdhesiveGeometry(magnetWidth, magnetHeight, adhesiveThickness);
            const adhesiveMaterial = new THREE.MeshPhysicalMaterial({
                color: 0x202020, // Even darker black tone
                metalness: 0.0,
                roughness: 0.05, // Very low roughness for strong reflections
                transmission: 0.7, // Lower transmission so color shows through
                thickness: 0.5,
                ior: 1.33, // Index of refraction for water
                clearcoat: 0.8, // High clearcoat for strong reflections
                clearcoatRoughness: 0.02, // Very smooth clearcoat
                transparent: true,
                opacity: 0.4, // Higher opacity so black color is visible
            });
            const adhesive = new THREE.Mesh(adhesiveGeometry, adhesiveMaterial);

            // Add subtle outline to adhesive (using simple box for clean edges only)
            const outlineGeometry = new THREE.BoxGeometry(magnetWidth, magnetHeight, adhesiveThickness);
            const adhesiveEdges = new THREE.EdgesGeometry(outlineGeometry);
            const adhesiveOutlineMaterial = new THREE.LineBasicMaterial({
                color: 0x888888,
                opacity: 0.3,
                transparent: true,
                linewidth: 1
            });
            const adhesiveOutline = new THREE.LineSegments(adhesiveEdges, adhesiveOutlineMaterial);

            // Group and position
            const magnetGroup = new THREE.Group();
            magnet.position.z = adhesiveThickness / 2;
            adhesive.position.z = -magnetThickness / 2;
            adhesiveOutline.position.copy(adhesive.position);
            magnetGroup.add(magnet);
            magnetGroup.add(adhesive);
            magnetGroup.add(adhesiveOutline);
            magnetGroup.position.set(pos.x, pos.y, offsetZ);

            this.rubberMagnetGrid.push(magnetGroup);
            this.scene.add(magnetGroup);
        });

        // Don't apply any image to magnets - keep them dark
    }



    clearMagnetGrid() {
        this.rubberMagnetGrid.forEach(magnet => {
            this.scene.remove(magnet);
        });
        this.rubberMagnetGrid = [];
    }

    updatePosterImage(imageUrl) {
        if (!imageUrl) {
            console.warn('No image URL provided');
            return;
        }

        this.currentImageUrl = imageUrl;

        // Handle grid mode
        if (this.isGridMode && this.metalPosterGrid.length > 0) {
            this.applySplitImageToGrid(imageUrl);
            return;
        }

        if (!this.metalPoster) {
            console.warn('Metal poster not created yet, will load image after poster creation');
            return;
        }

        const buildMaterials = (entry) => {
            if (!entry || !this.metalPoster) return;

            // Front face reacts to light: white-ink areas read glossy, colours
            // read matte, via the cached roughness map.
            const frontMaterial = new THREE.MeshStandardMaterial({
                map: entry.texture,
                color: 0xffffff,
                metalness: 0.0,
                roughness: 1.0,
                roughnessMap: entry.glossMap,
                envMap: this.envMap || null,
                envMapIntensity: 0.6
            });

            const backMaterial = new THREE.MeshStandardMaterial({
                color: 0xffffff,
                metalness: 0.7,
                roughness: 0.3,
            });
            // Synchronous once decoded, so the back is never briefly blank.
            this.applyBackTexture(backMaterial);

            const edgeMaterial = this.metalPoster.material[2] || new THREE.MeshStandardMaterial({
                color: 0x888888,
                metalness: 0.8,
                roughness: 0.2,
            });
            this.metalPoster.material = [backMaterial, frontMaterial, edgeMaterial];

            // MAGNET-ONLY: keep the poster hidden even after its texture loads.
            if (this.magnetOnly) this.metalPoster.visible = false;
        };

        // Take the cached entry on the SAME tick when we have one. Going through
        // .then() even on a cache hit would defer to a microtask, which is enough
        // to let a frame render with the old material still in place.
        const cached = this._frontTexCache.get(imageUrl);
        if (cached) {
            buildMaterials(cached);
            return;
        }
        this.loadFrontTexture(imageUrl).then(buildMaterials);
    }

    updateMetalTexture(metalTextureUrl) {
        if (!metalTextureUrl) {
            console.warn('No metal texture URL provided');
            return;
        }

        this.metalTextureUrl = metalTextureUrl;

        // Handle grid mode
        if (this.isGridMode && this.metalPosterGrid.length > 0) {
            this.applyMetalTextureToGrid(metalTextureUrl);
            return;
        }

        if (!this.metalPoster) {
            console.warn('Metal poster not created yet, will load metal texture after poster creation');
            return;
        }

        // Only a genuinely different URL should throw the decoded texture away.
        if (this._backTexUrl && this._backTexUrl !== metalTextureUrl) {
            this._backTex = null;
            this._backTexPromise = null;
        }
        this.applyBackTexture(this.metalPoster.material[0]);
    }

    updateDimensions(width, height, thickness) {
        console.log(`Updating dimensions to: ${width} x ${height} x ${thickness}`);

        // If coming from grid mode, transition back to single poster
        if (this.isGridMode) {
            this.transitionFromGridToSingle(width, height, thickness);
            return;
        }

        if (!this.metalPoster) {
            console.warn('Metal poster not created yet, storing dimensions for later');
            this.posterWidth = width;
            this.posterHeight = height;
            this.posterThickness = thickness;
            return;
        }

        // Store current dimensions for animation BEFORE updating
        const currentWidth = this.posterWidth;
        const currentHeight = this.posterHeight;
        const currentThickness = this.posterThickness;

        // Store new dimensions
        this.posterWidth = width;
        this.posterHeight = height;
        this.posterThickness = thickness;

        // If in magnet view mode, update magnet as well
        if (this.viewMode === 'magnet') {
            this.updateMagnetDimensions(width, height, thickness);
        }

        // Store target dimensions
        const targetWidth = width;
        const targetHeight = height;
        const targetThickness = thickness;

        // Animation settings
        const duration = 500; // 500ms transition
        const startTime = Date.now();

        // Easing function (ease-out)
        const easeOut = (t) => 1 - Math.pow(1 - t, 3);

        const animateTransition = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeOut(progress);

            // Interpolate dimensions
            const newWidth = currentWidth + (targetWidth - currentWidth) * easedProgress;
            const newHeight = currentHeight + (targetHeight - currentHeight) * easedProgress;
            const newThickness = currentThickness + (targetThickness - currentThickness) * easedProgress;

            // Update the poster scale to create smooth transition
            this.metalPoster.scale.set(
                newWidth / currentWidth,
                newHeight / currentHeight,
                newThickness / currentThickness
            );

            if (progress < 1) {
                // Continue animation
                requestAnimationFrame(animateTransition);
            } else {
                // Animation complete - update actual geometry
                this.finalizeNewDimensions(targetWidth, targetHeight, targetThickness);
            }
        };

        // Start the animation
        animateTransition();
    }

    finalizeNewDimensions(width, height, thickness) {
        // Store new dimensions
        this.posterWidth = width;
        this.posterHeight = height;
        this.posterThickness = thickness;

        // Store current poster state (position, rotation) if it exists
        const currentPosition = this.metalPoster ? {
            x: this.metalPoster.position.x,
            y: this.metalPoster.position.y,
            z: this.metalPoster.position.z
        } : { x: 0, y: 0, z: 0 };

        const currentRotation = this.metalPoster ? {
            x: this.metalPoster.rotation.x,
            y: this.metalPoster.rotation.y,
            z: this.metalPoster.rotation.z
        } : { x: 0, y: 0, z: 0 };

        // Remove old poster from scene
        this.scene.remove(this.metalPoster);

        // Create new geometry with updated dimensions
        const geometry = this.createRoundedBoxGeometry(
            this.posterWidth,
            this.posterHeight,
            this.posterThickness,
            0.2 // 2mm corner radius
        );

        // Create fresh materials to avoid mapping issues
        const frontMaterial = new THREE.MeshLambertMaterial({
            color: 0xffffff,
        });

        const backMaterial = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            metalness: 0.7,
            roughness: 0.3,
        });
        // Texture the back BEFORE the mesh reaches the scene, so the rebuilt
        // plate never renders a frame as a bare white slab.
        this.applyBackTexture(backMaterial);

        const edgeMaterial = new THREE.MeshStandardMaterial({
            color: 0x888888,
            metalness: 0.8,
            roughness: 0.2,
        });

        // Material order: [back, front, sides]
        const materials = [backMaterial, frontMaterial, edgeMaterial];

        // Create new mesh with updated geometry
        this.metalPoster = new THREE.Mesh(geometry, materials);
        this.metalPoster.castShadow = false; // Don't cast shadows on magnet
        this.metalPoster.receiveShadow = true;

        // MAGNET-ONLY: keep the rebuilt poster hidden.
        if (this.magnetOnly) this.metalPoster.visible = false;

        // Reset scale to 1 (since we now have the correct geometry)
        this.metalPoster.scale.set(1, 1, 1);

        // Restore previous position and rotation (maintains slideshow position)
        this.metalPoster.position.set(currentPosition.x, currentPosition.y, currentPosition.z);
        this.metalPoster.rotation.set(currentRotation.x, currentRotation.y, currentRotation.z);

        // Add updated poster to scene
        this.scene.add(this.metalPoster);

        // Reapply current product image if it exists
        if (this.currentImageUrl) {
            this.updatePosterImage(this.currentImageUrl);
        }

        // Reapply metal texture if it exists
        if (this.metalTextureUrl) {
            this.updateMetalTexture(this.metalTextureUrl);
        }

        console.log(`Dimensions finalized to ${width} x ${height} x ${thickness} with preserved position`);
        console.log(`Restored position: (${currentPosition.x}, ${currentPosition.y}, ${currentPosition.z})`);
    }

    /**
     * Force the rubber magnet to a specific size, overriding the width-based
     * rule above. Call with (null) to hand control back to that rule.
     *
     * Added for the Mini (A4) plate, which needs a 2.5 x 2.5 in magnet — the
     * existing rule only distinguishes 11.13 (2.5 x 5) from everything else
     * (5 x 5), so Mini would otherwise inherit the 5 x 5 magnet.
     *
     *   viewer.setMagnetSize(2.5, 2.5);   // mini
     *   viewer.setMagnetSize(null);       // back to the default rule
     */
    setMagnetSize(width, height) {
        if (width == null) {
            this.magnetSizeOverride = null;
        } else {
            this.magnetSizeOverride = { width: width, height: height == null ? width : height };
        }
        // Rebuild only if a magnet is already on screen, so this is safe to
        // call before the first size is applied.
        if (this.viewMode === 'magnet' && this.rubberMagnet) {
            this.updateMagnetDimensions(this.posterWidth, this.posterHeight, this.posterThickness);
        }
        return this;
    }

    /**
     * Set the rotation of the BACK (branded metal) texture.
     *
     *   viewer.setBackTextureRotation(Math.PI);      // portrait plate (default)
     *   viewer.setBackTextureRotation(Math.PI / 2);  // landscape plate
     *
     * Stored so that every later texture load applies it immediately, and also
     * pushed onto whatever is already on screen so a live orientation switch
     * does not have to wait for a reload. Setting it at load time is what
     * removes the flash of wrongly-oriented artwork.
     */
    setBackTextureRotation(rad) {
        if (typeof rad !== 'number' || !isFinite(rad)) return this;
        this.backTextureRotation = rad;

        const applyTo = (material) => {
            if (!material || !material.map) return;
            if (material.map.rotation === rad) return;   // already correct
            if (material.map.center && material.map.center.set) {
                material.map.center.set(0.5, 0.5);
            }
            material.map.rotation = rad;
            material.map.needsUpdate = true;
            material.needsUpdate = true;
        };

        // The cached texture is the one every back face shares, so setting it
        // here is what actually turns them all. Guarded, because a section may
        // call this repeatedly and each needsUpdate is a GPU re-upload.
        if (this._backTex && this._backTex.rotation !== rad) {
            if (this._backTex.center && this._backTex.center.set) this._backTex.center.set(0.5, 0.5);
            this._backTex.rotation = rad;
            this._backTex.needsUpdate = true;
        }

        // The back sits in a different slot depending on how the plate was
        // built: [back, front, edges] on the extruded one, [R,L,T,B, front,
        // back] on the rounded-box one. Pick by array shape — a hardcoded 0
        // would paint an EDGE in the second case.
        const backOf = (mesh) => {
            if (!mesh || !Array.isArray(mesh.material)) return null;
            return mesh.material[mesh.material.length >= 6 ? 5 : 0];
        };

        applyTo(backOf(this.metalPoster));
        this.metalPosterGrid.forEach((poster) => applyTo(backOf(poster)));
        return this;
    }

    updateMagnetDimensions(width, height, thickness) {
        console.log(`Updating magnet dimensions to: ${width} x ${height} x ${thickness}`);

        // Check if we're transitioning to/from grid mode
        const wasGridMode = this.rubberMagnet && this.rubberMagnet.children && this.rubberMagnet.children.length > 1;
        const willBeGridMode = this.isGridMode;

        // Animation settings for magnet transition
        const duration = 400; // 400ms transition
        const startTime = Date.now();

        // Store current magnet (if exists)
        const oldMagnet = this.rubberMagnet;

        // Create new magnet with new dimensions
        this.createRubberMagnet();
        const newMagnet = this.rubberMagnet;

        // Start new magnet scaled down to 0
        if (willBeGridMode && newMagnet.children) {
            // For grid magnets, scale each child
            newMagnet.children.forEach(childMagnet => {
                childMagnet.scale.set(0, 0, 0);
            });
        } else {
            // For single magnet
            newMagnet.scale.set(0, 0, 0);
        }

        // Easing function (ease-out)
        const easeOut = (t) => 1 - Math.pow(1 - t, 3);

        const animateTransition = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeOut(progress);

            // Fade out old magnet
            if (oldMagnet) {
                const oldScale = 1 - easedProgress;
                if (wasGridMode && oldMagnet.children) {
                    // For old grid magnets, scale each child
                    oldMagnet.children.forEach(childMagnet => {
                        childMagnet.scale.set(oldScale, oldScale, oldScale);
                    });
                } else {
                    // For old single magnet
                    oldMagnet.scale.set(oldScale, oldScale, oldScale);
                }
            }

            // Fade in new magnet
            if (willBeGridMode && newMagnet.children) {
                // For new grid magnets, scale each child
                newMagnet.children.forEach(childMagnet => {
                    childMagnet.scale.set(easedProgress, easedProgress, easedProgress);
                });
            } else {
                // For new single magnet
                newMagnet.scale.set(easedProgress, easedProgress, easedProgress);
            }

            if (progress < 1) {
                requestAnimationFrame(animateTransition);
            } else {
                // Animation complete - remove old magnet
                if (oldMagnet) {
                    this.scene.remove(oldMagnet);
                }
                console.log('Magnet dimension transition complete');
            }
        };

        // Start the animation
        animateTransition();
    }

    createLargeMagnetGrid() {
        console.log('Creating 4 magnets for large grid mode');

        // Clear any existing magnet
        if (this.rubberMagnet) {
            this.scene.remove(this.rubberMagnet);
            this.rubberMagnet = null;
        }

        // Use 5x5 inch magnets for large size
        const magnetWidth = 5;
        const magnetHeight = 5;
        const magnetThickness = 0.2; // 2mm
        const adhesiveThickness = 0.15; // 1.5mm

        // Grid layout positions matching the metal poster grid
        const gap = 0.1;
        const posterWidth = this.gridPlateWidth;
        const posterHeight = this.gridPlateHeight;

        // MAGNET-ONLY: with no poster behind them, space the 4 magnets by their
        // own size (tight 2x2) instead of the poster's footprint, so they stay
        // centered and on-screen. Poster mode keeps the poster-matched spacing.
        const spacingW = this.magnetOnly ? magnetWidth : posterWidth;
        const spacingH = this.magnetOnly ? magnetHeight : posterHeight;
        const spacingGap = this.magnetOnly ? 0.4 : gap;

        const positions = [
            { x: -(spacingW/2 + spacingGap/2), y: spacingH/2 + spacingGap/2, quadrant: 'topLeft' },    // Top-Left
            { x: spacingW/2 + spacingGap/2, y: spacingH/2 + spacingGap/2, quadrant: 'topRight' },     // Top-Right
            { x: -(spacingW/2 + spacingGap/2), y: -(spacingH/2 + spacingGap/2), quadrant: 'bottomLeft' }, // Bottom-Left
            { x: spacingW/2 + spacingGap/2, y: -(spacingH/2 + spacingGap/2), quadrant: 'bottomRight' }   // Bottom-Right
        ];

        // Create container group for all magnets
        const magnetGridGroup = new THREE.Group();
        // MAGNET-ONLY: center at z=0 (no poster to sit behind).
        magnetGridGroup.position.z = this.magnetOnly ? 0 : -10; // Position behind metal posters (increased distance)

        positions.forEach((pos, index) => {
            // Create rubber magnet (very dark, matte)
            const magnetGeometry = new THREE.BoxGeometry(magnetWidth, magnetHeight, magnetThickness);
            const magnetMaterial = new THREE.MeshLambertMaterial({
                color: 0x020202, // Even darker, almost pure black
                roughness: 0.98, // Very matte
            });
            const magnetMesh = new THREE.Mesh(magnetGeometry, magnetMaterial);
            magnetMesh.castShadow = true;
            magnetMesh.receiveShadow = true;

            // Create bumpy adhesive layer (pure transparent with reflections)
            const adhesiveGeometry = this.createBumpyAdhesiveGeometry(magnetWidth, magnetHeight, adhesiveThickness);
            const adhesiveMaterial = new THREE.MeshPhysicalMaterial({
                color: 0x202020, // Even darker black tone
                metalness: 0.0,
                roughness: 0.05, // Very low roughness for strong reflections
                transmission: 0.7, // Lower transmission so color shows through
                thickness: 0.5,
                ior: 1.33, // Index of refraction for water
                clearcoat: 0.8, // High clearcoat for strong reflections
                clearcoatRoughness: 0.02, // Very smooth clearcoat
                transparent: true,
                opacity: 0.4, // Higher opacity so black color is visible
            });

            const adhesiveLayer = new THREE.Mesh(adhesiveGeometry, adhesiveMaterial);
            adhesiveLayer.position.z = -magnetThickness / 2;
            adhesiveLayer.castShadow = false;
            adhesiveLayer.receiveShadow = true;

            // Add subtle outline to adhesive (using simple box for clean edges only)
            const outlineGeometry = new THREE.BoxGeometry(magnetWidth, magnetHeight, adhesiveThickness);
            const adhesiveEdges = new THREE.EdgesGeometry(outlineGeometry);
            const adhesiveOutlineMaterial = new THREE.LineBasicMaterial({
                color: 0x888888,
                opacity: 0.3,
                transparent: true,
                linewidth: 1
            });
            const adhesiveOutline = new THREE.LineSegments(adhesiveEdges, adhesiveOutlineMaterial);
            adhesiveOutline.position.copy(adhesiveLayer.position);

            // Group magnet, adhesive, and outline for this position
            const singleMagnetGroup = new THREE.Group();
            singleMagnetGroup.add(magnetMesh);
            singleMagnetGroup.add(adhesiveLayer);
            singleMagnetGroup.add(adhesiveOutline);
            singleMagnetGroup.position.set(pos.x, pos.y, 0);

            magnetGridGroup.add(singleMagnetGroup);
        });

        this.scene.add(magnetGridGroup);
        this.rubberMagnet = magnetGridGroup; // Store the group as the rubber magnet

        console.log('Created 4 magnets for large grid mode');
    }

    // Large, portrait: four 16x23 plates -> 32x46 in total.
    updateToLargeGrid() {
        this.buildLargeGrid(16, 23);
    }

    // Large, landscape: four 23x16 plates -> 46x32 in total.
    //
    // This used to be missing entirely. The product sections test for it, do not
    // find it, fall past the portrait branch as well, and land on
    // updateDimensions(46, 32) — which builds ONE big plate. That is why
    // landscape Large showed a single poster instead of four.
    updateToLargeGridLandscape() {
        this.buildLargeGrid(23, 16);
    }

    buildLargeGrid(plateWidth, plateHeight) {
        // Already showing exactly this grid — nothing to do.
        if (this.isGridMode &&
            this.gridPlateWidth === plateWidth &&
            this.gridPlateHeight === plateHeight) {
            console.log('Already in this grid orientation');
            return;
        }

        this.showLargePaymentMessage();

        // Switching orientation while ALREADY in grid mode. The old code
        // early-returned on isGridMode, so portrait -> landscape did nothing at
        // all. Rebuild the grid in place instead (no fade: the plates are being
        // replaced, not travelling anywhere).
        if (this.isGridMode) {
            this.gridPlateWidth = plateWidth;
            this.gridPlateHeight = plateHeight;
            this.clearGrid();
            this.createLargePosterGrid();

            if (this.viewMode === 'magnet') {
                if (this.rubberMagnet) {
                    this.scene.remove(this.rubberMagnet);
                    this.rubberMagnet = null;
                }
                this.clearMagnetGrid();
                this.createLargeMagnetGrid();
            }
            console.log(`Rebuilt large grid as 2x2 of ${plateWidth} x ${plateHeight} plates`);
            return;
        }

        this.gridPlateWidth = plateWidth;
        this.gridPlateHeight = plateHeight;
        this.transitionFromSingleToGrid(plateWidth * 2, plateHeight * 2, 0.05);
    }

    transitionFromSingleToGrid(width, height, thickness) {
        console.log(`Transitioning from single to large grid: ${width} x ${height} x ${thickness}`);

        // Handle magnet transition if in magnet view mode
        const isInMagnetMode = this.viewMode === 'magnet';
        const oldSingleMagnet = isInMagnetMode ? this.rubberMagnet : null;

        // Animation settings - smooth transition
        const singleOutDuration = 400;  // Single poster disappears
        const gridInDuration = 600;     // Grid appears
        const delayBetween = 150;       // Small delay between animations

        // Phase 1: Fade out the single poster
        const startTime = Date.now();
        const singlePoster = this.metalPoster;

        const fadeOutSingle = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / singleOutDuration, 1);
            const easeInProgress = progress * progress; // Ease-in for smooth fade

            // Scale down single poster
            if (singlePoster) {
                const scale = 1 - easeInProgress;
                singlePoster.scale.set(scale, scale, scale);
            }

            // Scale down single magnet if in magnet mode
            if (isInMagnetMode && oldSingleMagnet) {
                const scale = 1 - easeInProgress;
                oldSingleMagnet.scale.set(scale, scale, scale);
            }

            if (progress < 1) {
                requestAnimationFrame(fadeOutSingle);
            } else {
                // Single is gone, start grid creation after delay
                setTimeout(() => {
                    this.createAndShowGrid(width, height, thickness, isInMagnetMode, oldSingleMagnet);
                }, delayBetween);
            }
        };

        fadeOutSingle();
    }

    createAndShowGrid(width, height, thickness, isInMagnetMode = false, oldSingleMagnet = null) {
        // Clear the single poster
        if (this.metalPoster) {
            this.scene.remove(this.metalPoster);
            this.metalPoster = null;
        }
        this.isGridMode = true;

        // Create the grid
        this.createLargePosterGrid();

        // If in slideshow mode, position grid posters at slideshow position (higher for large size)
        if (this.viewMode === 'slideshow') {
            const slideshowPositions = this.getSlideshowPositions();
            this.metalPosterGrid.forEach((poster, index) => {
                const targetPos = slideshowPositions[index];
                poster.position.set(targetPos.x, targetPos.y, targetPos.z);
                console.log(`Grid poster ${index} positioned at: (${targetPos.x.toFixed(2)}, ${targetPos.y.toFixed(2)}, ${targetPos.z.toFixed(2)})`);
            });
            console.log('*** DIRECT GRID CREATION: Positioned grid posters at exact slideshow positions using shared function ***');
        }

        // Create new magnet grid if in magnet mode
        let newMagnetGrid = null;
        if (isInMagnetMode) {
            this.createLargeMagnetGrid();
            newMagnetGrid = this.rubberMagnet;
            if (newMagnetGrid && newMagnetGrid.children) {
                newMagnetGrid.children.forEach(childMagnet => {
                    childMagnet.scale.set(0, 0, 0);
                });
            }
        }

        // Start grid posters at scale 0
        this.metalPosterGrid.forEach(poster => {
            poster.scale.set(0, 0, 0);
        });

        // Phase 2: Pop in the grid with smooth ease-out
        const gridInDuration = 600;
        const startTime = Date.now();

        const popInGrid = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / gridInDuration, 1);
            const easeOutProgress = 1 - Math.pow(1 - progress, 3); // Smooth ease-out

            // Scale up grid posters smoothly
            this.metalPosterGrid.forEach(poster => {
                poster.scale.set(easeOutProgress, easeOutProgress, easeOutProgress);
            });

            // Scale up magnet grid if in magnet mode
            if (isInMagnetMode && newMagnetGrid && newMagnetGrid.children) {
                newMagnetGrid.children.forEach(childMagnet => {
                    childMagnet.scale.set(easeOutProgress, easeOutProgress, easeOutProgress);
                });
            }

            if (progress < 1) {
                requestAnimationFrame(popInGrid);
            } else {
                // Animation complete - clean up old single magnet
                if (isInMagnetMode && oldSingleMagnet) {
                    this.scene.remove(oldSingleMagnet);
                }

                // Apply textures
                if (this.currentImageUrl) {
                    this.updatePosterImage(this.currentImageUrl);
                }
                if (this.metalTextureUrl) {
                    this.updateMetalTexture(this.metalTextureUrl);
                }

                console.log('Single to grid transition complete with slideshow positioning');
            }
        };

        popInGrid();
    }

    createLargePosterGrid() {
        console.log('Creating 2x2 poster grid');

        // Clear existing grid
        this.clearGrid();

        // Grid layout positions for 2x2 arrangement.
        // Plate size comes from gridPlateWidth/Height so the same code builds a
        // portrait grid (16x23 plates) or a landscape one (23x16 plates).
        const gap = 0.1; // Reduced gap between posters
        const posterWidth = this.gridPlateWidth;
        const posterHeight = this.gridPlateHeight;
        const thickness = 0.05;

        const positions = [
            { x: -(posterWidth/2 + gap/2), y: posterHeight/2 + gap/2, quadrant: 'topLeft' },    // Top-Left
            { x: posterWidth/2 + gap/2, y: posterHeight/2 + gap/2, quadrant: 'topRight' },     // Top-Right
            { x: -(posterWidth/2 + gap/2), y: -(posterHeight/2 + gap/2), quadrant: 'bottomLeft' }, // Bottom-Left
            { x: posterWidth/2 + gap/2, y: -(posterHeight/2 + gap/2), quadrant: 'bottomRight' }   // Bottom-Right
        ];

        // Create 4 poster objects
        positions.forEach((pos, index) => {
            const geometry = this.createRoundedBoxGeometry(posterWidth, posterHeight, thickness, 0.2);

            // Create materials for ExtrudeGeometry (same structure as single poster)
            const materials = [];

            // Front face material (gets a quarter of the artwork). Standard,
            // not Lambert, so the grid reacts to the light exactly like the
            // single plate does — Lambert is why Large looked flat.
            const frontMaterial = new THREE.MeshStandardMaterial({
                color: 0xffffff,
                metalness: 0.0,
                roughness: 1.0,
                envMap: this.envMap || null,
                envMapIntensity: 0.6
            });

            // Back face: brushed metal, same numbers as the single plate.
            const backMaterial = new THREE.MeshStandardMaterial({
                color: 0xffffff,
                metalness: 0.7,
                roughness: 0.3,
            });
            // Textured up front — all four plates share one decoded texture, so
            // an orientation rebuild shows the branded back immediately.
            this.applyBackTexture(backMaterial);

            // Edge materials (metallic)
            const edgeMaterial = new THREE.MeshStandardMaterial({
                color: 0x888888,
                metalness: 0.8,
                roughness: 0.2,
            });

            // Custom material order to match groups: [back, front, sides]
            materials[0] = backMaterial; // Back face (metal)
            materials[1] = frontMaterial; // Front face (image)
            materials[2] = edgeMaterial; // Side faces (metal edges)

            // Create mesh
            const poster = new THREE.Mesh(geometry, materials);
            poster.position.set(pos.x, pos.y, 0);
            poster.castShadow = false; // Don't cast shadows on magnet
            poster.receiveShadow = true;
            poster.userData = { quadrant: pos.quadrant, index: index };

            // MAGNET-ONLY: keep grid posters hidden.
            if (this.magnetOnly) poster.visible = false;

            this.metalPosterGrid.push(poster);
            this.scene.add(poster);
        });

        // Apply split image to grid
        if (this.currentImageUrl) {
            this.applySplitImageToGrid(this.currentImageUrl);
        }

        // Apply metal texture to all back faces
        if (this.metalTextureUrl) {
            this.applyMetalTextureToGrid(this.metalTextureUrl);
        }

        console.log('Grid created with 4 posters');
    }

    applySplitImageToGrid(imageUrl) {
        console.log('Splitting image across 2x2 grid');

        const textureLoader = new THREE.TextureLoader();
        textureLoader.setCrossOrigin('anonymous');

        textureLoader.load(
            imageUrl,
            (texture) => {
                console.log('Full texture loaded, creating quadrants');

                // Create 4 canvas elements for the 4 quadrants
                const canvases = this.createImageQuadrants(texture.image);

                // Apply each quadrant to the corresponding poster
                canvases.forEach((canvas, index) => {
                    if (this.metalPosterGrid[index]) {
                        const quadrantTexture = new THREE.CanvasTexture(canvas);
                        quadrantTexture.wrapS = THREE.ClampToEdgeWrapping;
                        quadrantTexture.wrapT = THREE.ClampToEdgeWrapping;
                        quadrantTexture.minFilter = THREE.LinearFilter;
                        quadrantTexture.magFilter = THREE.LinearFilter;
                        quadrantTexture.encoding = THREE.sRGBEncoding;
                        quadrantTexture.flipY = true; // Fix upside down image

                        // Front face: same material as the single plate, so the
                        // 2x2 grid keeps the sheen and the light response.
                        // (It used to keep the flat Lambert material, which is
                        // why Large looked like a plain printed picture.)
                        const mesh = this.metalPosterGrid[index];
                        const mats = Array.isArray(mesh.material) ? mesh.material.slice() : [mesh.material];
                        mats[1] = new THREE.MeshStandardMaterial({
                            map: quadrantTexture,
                            color: 0xffffff,
                            metalness: 0.0,
                            roughness: 1.0,
                            roughnessMap: this.generateGlossMap(canvas),
                            envMap: this.envMap || null,
                            envMapIntensity: 0.6
                        });
                        mesh.material = mats;

                        // Back face: rebuild as brushed metal (an older plate may
                        // still carry the flat Lambert material).
                        if (!mats[0] || !mats[0].isMeshStandardMaterial) {
                            mats[0] = new THREE.MeshStandardMaterial({
                                color: 0xffffff,
                                metalness: 0.7,
                                roughness: 0.3
                            });
                        }
                        this.applyBackTexture(mats[0]);
                    }
                });

                console.log('Split image applied to all grid posters');
            },
            undefined,
            (error) => {
                console.error('Error loading image for grid:', error);
            }
        );
    }

    createImageQuadrants(image) {
        const quadrants = [];
        const originalWidth = image.width;
        const originalHeight = image.height;
        const halfWidth = originalWidth / 2;
        const halfHeight = originalHeight / 2;

        // Create canvas for each quadrant
        const quadrantConfigs = [
            { sx: 0, sy: 0, name: 'topLeft' },                    // Top-Left
            { sx: halfWidth, sy: 0, name: 'topRight' },           // Top-Right
            { sx: 0, sy: halfHeight, name: 'bottomLeft' },        // Bottom-Left
            { sx: halfWidth, sy: halfHeight, name: 'bottomRight' } // Bottom-Right
        ];

        quadrantConfigs.forEach(config => {
            const canvas = document.createElement('canvas');
            canvas.width = halfWidth;
            canvas.height = halfHeight;
            const ctx = canvas.getContext('2d');

            // Draw the specific quadrant
            ctx.drawImage(
                image,
                config.sx, config.sy, halfWidth, halfHeight,  // source
                0, 0, halfWidth, halfHeight                     // destination
            );

            quadrants.push(canvas);
            console.log(`Created ${config.name} quadrant: ${halfWidth}x${halfHeight}`);
        });

        return quadrants;
    }

    applyMetalTextureToGrid(metalTextureUrl) {
        // One shared texture for all four plates. This used to fire four
        // separate TextureLoader requests for the same URL.
        if (metalTextureUrl && metalTextureUrl !== this.metalTextureUrl) {
            this.metalTextureUrl = metalTextureUrl;
            this._backTex = null;
            this._backTexPromise = null;
        }
        this.metalPosterGrid.forEach((poster) => this.applyBackTexture(poster.material[0]));
    }

    clearGrid() {
        // Remove all grid posters from scene
        this.metalPosterGrid.forEach(poster => {
            this.scene.remove(poster);
        });
        this.metalPosterGrid = [];
    }

    transitionFromGridToSingle(width, height, thickness) {
        console.log(`Transitioning from grid to single poster: ${width} x ${height} x ${thickness}`);

        // Handle magnet transition if in magnet view mode
        const isInMagnetMode = this.viewMode === 'magnet';
        const oldMagnetGrid = isInMagnetMode ? this.rubberMagnet : null;

        // Animation settings - much faster for clean pop effect
        const gridOutDuration = 200;  // Grid disappears quickly
        const singleInDuration = 300; // Single poster appears with slight delay
        const delayBetween = 100;     // Small delay between animations

        // Phase 1: Quickly fade out the grid
        const startTime = Date.now();

        const fadeOutGrid = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / gridOutDuration, 1);
            const easeInProgress = progress * progress; // Ease-in for quick disappear

            // Scale down grid posters quickly
            this.metalPosterGrid.forEach(poster => {
                const scale = 1 - easeInProgress;
                poster.scale.set(scale, scale, scale);
            });

            // Scale down magnet grid if in magnet mode
            if (isInMagnetMode && oldMagnetGrid && oldMagnetGrid.children) {
                oldMagnetGrid.children.forEach(childMagnet => {
                    const scale = 1 - easeInProgress;
                    childMagnet.scale.set(scale, scale, scale);
                });
            }

            if (progress < 1) {
                requestAnimationFrame(fadeOutGrid);
            } else {
                // Grid is gone, start single poster creation after delay
                setTimeout(() => {
                    this.createAndShowSinglePoster(width, height, thickness, isInMagnetMode, oldMagnetGrid);
                }, delayBetween);
            }
        };

        fadeOutGrid();
    }

    createAndShowSinglePoster(width, height, thickness, isInMagnetMode = false, oldMagnetGrid = null) {
        // Clear the grid completely
        this.clearGrid();
        this.isGridMode = false;

        // Hide payment message when switching away from large size
        this.hideLargePaymentMessage();

        // Update stored dimensions
        this.posterWidth = width;
        this.posterHeight = height;
        this.posterThickness = thickness;

        // Create new single magnet if in magnet mode
        let newSingleMagnet = null;
        if (isInMagnetMode) {
            this.createRubberMagnet();
            newSingleMagnet = this.rubberMagnet;
            newSingleMagnet.scale.set(0, 0, 0); // Start invisible
        }

        // Create the single poster with rounded corners
        const geometry = this.createRoundedBoxGeometry(width, height, thickness, 0.2);

        // Create materials
        const materials = [];
        const frontMaterial = new THREE.MeshLambertMaterial({ color: 0xffffff });
        const backMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, metalness: 0.7, roughness: 0.3 });
        this.applyBackTexture(backMaterial);
        const edgeMaterial = new THREE.MeshStandardMaterial({ color: 0x888888, metalness: 0.8, roughness: 0.2 });

        materials[0] = edgeMaterial; // Right
        materials[1] = edgeMaterial; // Left
        materials[2] = edgeMaterial; // Top
        materials[3] = edgeMaterial; // Bottom
        materials[4] = frontMaterial; // Front
        materials[5] = backMaterial; // Back

        const newSinglePoster = new THREE.Mesh(geometry, materials);
        newSinglePoster.castShadow = false; // Don't cast shadows on magnet
        newSinglePoster.receiveShadow = true;
        newSinglePoster.scale.set(0, 0, 0); // Start invisible

        // MAGNET-ONLY: keep the rebuilt single poster hidden.
        if (this.magnetOnly) newSinglePoster.visible = false;

        // Position according to current view mode
        if (this.viewMode === 'slideshow') {
            const slideshowPositions = this.getSlideshowPositions();
            const targetPos = slideshowPositions[0]; // Single poster uses first position
            newSinglePoster.position.set(targetPos.x, targetPos.y, targetPos.z);
            console.log('Positioned new single poster at slideshow position using shared function');
        } else {
            newSinglePoster.position.set(0, 0, 0); // Default position
        }

        this.scene.add(newSinglePoster);
        this.metalPoster = newSinglePoster;

        // Phase 2: Pop in the single poster with smooth ease-out
        const singleInDuration = 300;
        const startTime = Date.now();

        const popInSingle = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / singleInDuration, 1);
            const easeOutProgress = 1 - Math.pow(1 - progress, 3); // Smooth ease-out

            // Scale up single poster smoothly
            newSinglePoster.scale.set(easeOutProgress, easeOutProgress, easeOutProgress);

            // Scale up single magnet if in magnet mode
            if (isInMagnetMode && newSingleMagnet) {
                newSingleMagnet.scale.set(easeOutProgress, easeOutProgress, easeOutProgress);
            }

            if (progress < 1) {
                requestAnimationFrame(popInSingle);
            } else {
                // Animation complete - clean up old magnet grid
                if (isInMagnetMode && oldMagnetGrid) {
                    this.scene.remove(oldMagnetGrid);
                }

                // Apply textures
                if (this.currentImageUrl) {
                    this.updatePosterImage(this.currentImageUrl);
                }
                if (this.metalTextureUrl) {
                    this.updateMetalTexture(this.metalTextureUrl);
                }

                console.log('Grid to single pop transition complete with proper positioning');
            }
        };

        popInSingle();
    }

    setupControls() {
        this.controls = new THREE.OrbitControls(this.camera, this.renderer.domElement);

        // Configure controls
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.enableZoom = true;
        this.controls.enablePan = false; // Disable panning to keep object centered

        // Set zoom limits
        this.controls.minDistance = 20;
        this.controls.maxDistance = 80;

        // Set rotation limits
        this.controls.maxPolarAngle = Math.PI; // Allow full vertical rotation
        this.controls.minPolarAngle = 0;

        // Auto-rotate disabled by default
        this.controls.autoRotate = false;
        this.controls.autoRotateSpeed = 0.5;

        // Set target to center
        this.controls.target.set(0, 0, 0);
        this.controls.update();
    }

    setupEventListeners() {
        // Handle window resize
        window.addEventListener('resize', () => this.onWindowResize(), false);

        // Mouse interaction for info dots and parallax
        this.canvas.addEventListener('mousemove', (event) => this.onMouseMove(event), false);
        this.canvas.addEventListener('click', (event) => this.onMouseClick(event), false);
        this.canvas.addEventListener('mouseleave', () => this.hideInfoBox(), false);

        // Touch support
        this.canvas.addEventListener('touchstart', (event) => this.onTouchStart(event), false);

        // Parallax hover detection
        this.isParallaxEnabled = false;
        this.canvas.addEventListener('mouseenter', () => {
            // Only enable parallax in metal view
            if (this.viewMode === 'metal') {
                this.isParallaxEnabled = true;
                console.log('Parallax enabled (metal view)');
            }
        });
        this.canvas.addEventListener('mouseleave', () => {
            this.isParallaxEnabled = false;
            console.log('Parallax disabled (retaining position)');
        });

        // Image selector functionality
        const imageOptions = document.querySelectorAll('.poster-option');
        imageOptions.forEach(option => {
            option.addEventListener('click', (e) => {
                // Remove active class from all options
                imageOptions.forEach(opt => opt.classList.remove('active'));

                // Add active class to clicked option
                e.target.classList.add('active');

                // Update poster image
                const imageUrl = e.target.getAttribute('data-image');
                this.updatePosterImage(imageUrl);
            });
        });

        // Set first image as active
        if (imageOptions.length > 0) {
            imageOptions[0].classList.add('active');
            const initialImage = imageOptions[0].getAttribute('data-image');
            this.updatePosterImage(initialImage);
        }
    }

    onMouseMove(event) {
        // Calculate mouse position in normalized device coordinates
        const rect = this.canvas.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        // Update raycaster
        this.raycaster.setFromCamera(this.mouse, this.camera);

        // Check for intersections with info dots
        const infoDots = [this.posterInfoDot, this.magnetInfoDot].filter(dot => dot);
        const intersects = this.raycaster.intersectObjects(infoDots, true);

        if (intersects.length > 0) {
            // Find the parent info dot
            let infoDot = intersects[0].object;
            while (infoDot.parent && !infoDot.userData.isInfoDot) {
                infoDot = infoDot.parent;
            }

            if (infoDot.userData.isInfoDot) {
                this.canvas.style.cursor = 'pointer';
                this.showInfoBox(infoDot, event.clientX, event.clientY);
            }
        } else {
            this.canvas.style.cursor = 'grab';
            this.hideInfoBox();
        }

        // Parallax effect if hovering over canvas and in metal view
        if (this.isParallaxEnabled && this.viewMode === 'metal') {
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;
            const deltaX = (event.clientX - rect.left) - centerX;
            const deltaY = (event.clientY - rect.top) - centerY;

            const maxRotation = Math.PI / 120; // Extremely subtle: 1.5 degrees max rotation
            this.targetRotationY = (deltaX / centerX) * maxRotation;
            this.targetRotationX = -(deltaY / centerY) * maxRotation;
        }
    }

    onMouseClick(event) {
        if (this.viewMode !== 'magnet') return;

        // Calculate mouse position in normalized device coordinates
        const rect = this.canvas.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        // Update raycaster
        this.raycaster.setFromCamera(this.mouse, this.camera);

        // Check for intersections with info dots
        const infoDots = [this.posterInfoDot, this.magnetInfoDot].filter(dot => dot);
        const intersects = this.raycaster.intersectObjects(infoDots, true);

        if (intersects.length > 0) {
            // Find the parent info dot
            let infoDot = intersects[0].object;
            while (infoDot.parent && !infoDot.userData.isInfoDot) {
                infoDot = infoDot.parent;
            }

            if (infoDot.userData.isInfoDot) {
                this.showInfoBox(infoDot, event.clientX, event.clientY);
                console.log('Clicked on info dot:', infoDot.userData.type);
            }
        }
    }

    // Add touch support for info dots
    onTouchStart(event) {
        if (this.viewMode !== 'magnet') return;

        // Simulate mousemove for touch
        const touch = event.touches[0];
        const fakeMouseEvent = {
            clientX: touch.clientX,
            clientY: touch.clientY,
        };
        this.onMouseMove(fakeMouseEvent);

        // Show info box on tap
        this.onMouseClick(fakeMouseEvent);
    }

    onWindowResize() {
        const width = this.container.clientWidth;
        const height = this.container.clientHeight;

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();

        this.renderer.setSize(width, height);
    }

    /* A 3D canvas that keeps rendering while it is scrolled away (or the tab is
       in the background) burns battery and steals main-thread time from the
       rest of the page while it is still loading. The loop keeps running, but
       it does no work and draws nothing until the canvas is on screen again. */
    setupVisibilityGate() {
        this._canSee = true;
        const update = () => {
            this._canSee = !document.hidden && this._onScreen !== false;
        };
        this._onScreen = true;
        document.addEventListener('visibilitychange', update);
        if ('IntersectionObserver' in window && this.canvas) {
            this._io = new IntersectionObserver((entries) => {
                entries.forEach((e) => { this._onScreen = e.isIntersecting; });
                update();
            }, { rootMargin: '150px' });
            this._io.observe(this.canvas);
        }
        update();
    }

    animate() {
        requestAnimationFrame(() => this.animate());

        if (this._canSee === false) return;   // off screen / background tab

        // Update controls
        this.controls.update();

        // Update info dot positions in magnet view
        if (this.viewMode === 'magnet') {
            this.updateInfoDotPositions();
        }

        // Subtle floating animation
        if (this.metalPoster) {
            this.metalPoster.position.y = Math.sin(Date.now() * 0.001) * 0.1;
        }

        // Smooth parallax rotation in animate loop
        if (this.viewMode === 'metal' || this.viewMode === 'magnet') {
            const lerpSpeed = this.viewMode === 'metal' ? 0.08 : 0.12; // Faster transition out in magnet view
            const posterObjects = this.isGridMode ? this.metalPosterGrid : [this.metalPoster];
            posterObjects.forEach(poster => {
                if (poster) {
                    // Always smoothly interpolate to target (retains position when mouse leaves)
                    this.currentRotationX += (this.targetRotationX - this.currentRotationX) * lerpSpeed;
                    this.currentRotationY += (this.targetRotationY - this.currentRotationY) * lerpSpeed;

                    poster.rotation.x = this.currentRotationX;
                    poster.rotation.y = this.currentRotationY;
                }
            });
        }

        // Debug slideshow plane every 60 frames (once per second at 60fps)
        if (this.viewMode === 'slideshow' && this.slideshowPlane && (Date.now() % 1000 < 16)) {
            console.log('=== SLIDESHOW DEBUG ===');
            console.log('Camera position:', this.camera.position);
            console.log('Slideshow plane position:', this.slideshowPlane.position);
            console.log('Distance to plane:', this.camera.position.distanceTo(this.slideshowPlane.position));
            console.log('Slideshow plane visible:', this.slideshowPlane.visible);
            console.log('Material map:', !!this.slideshowPlane.material.map);
            console.log('Scene children count:', this.scene.children.length);
        }

        // Render background first if exists
        if (this.backgroundScene && this.backgroundCamera) {
            this.renderer.autoClear = false;
            this.renderer.clear(true, true, true);
            this.renderer.render(this.backgroundScene, this.backgroundCamera);
        }

        // Render the main scene
        this.renderer.render(this.scene, this.camera);
    }

    // Public methods for external control
    enableAutoRotate() {
        this.controls.autoRotate = true;
    }

    disableAutoRotate() {
        this.controls.autoRotate = false;
    }

    resetCamera() {
        this.controls.reset();
    }

    setImage(imageUrl) {
        this.updatePosterImage(imageUrl);
    }

    isObjectInView(object) {
        if (!object || !this.camera) return false;

        const frustum = new THREE.Frustum();
        frustum.setFromProjectionMatrix(new THREE.Matrix4().multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse));

        const objPos = new THREE.Vector3();
        object.getWorldPosition(objPos);

        return frustum.containsPoint(objPos);
    }
}

// This will be initialized by the Shopify section, not here
console.log('Metal poster viewer JS loaded - waiting for section initialization');